/**
 * Entry point — the one place the seven modules meet.
 *
 * Nothing here renders, animates, scores or makes a sound. Every one of those
 * belongs to a module that already knows how to do it; this file's whole job is
 * to make them agree on order, on coordinates, and on who is allowed to write
 * what.
 *
 * ## The shape of it
 *
 * `GameEngine` is the only writer in the app. It publishes on the shared bus and
 * everything downstream reacts: `PileTotals`, `VFX`, `Audio` and the DOM `UI`
 * all subscribe themselves and need no help. The one thing that cannot be driven
 * straight off the bus is the *card choreography*, because the engine resolves a
 * play synchronously — `card:play` and `pile:resolve` land in the same tick —
 * while the animation of that play takes half a second. So this file keeps a
 * small sequencer: game state runs ahead, the visuals follow, and the two are
 * reconciled explicitly rather than hoped about.
 *
 * ## Reconciliation, not queueing
 *
 * A player who outruns the animation must never be made to wait for it. A new
 * play therefore *cuts* the old one: `Choreographer.fastForward()` finishes every
 * timeline instantly, the board is rebuilt from the engine's snapshot in one
 * frame, and the new card flies from there. A generation counter retires the
 * abandoned sequence so it cannot flip a card that has already been played.
 *
 * ## Two seams that had to be picked
 *
 * - **The flip squash.** `card.ts` turns the card in its shader with a real
 *   perspective divide; `choreography.ts` narrows the same card with `scale.x`.
 *   Both at once narrows it twice, so every sprite gets `squashFromFlip = false`
 *   and the choreographer keeps the squash. The shader keeps the trapezoid, the
 *   rotating normals and the travelling sheen.
 * - **Screen shake.** `Choreographer.shake()` and `VFX` both want it. VFX wins:
 *   it already owns the impulse, the decay and the bloom kick that goes with it,
 *   and `Stage.setShake()` inverts itself inside `toDesignSpace()` so taps still
 *   land during an impact. The choreographer is constructed with
 *   `autoShake: false` so a bust does not shake the table twice.
 *
 * ## The camera and the hit test
 *
 * `Stage` slides the layers under the pointer for parallax, which means the pile
 * a player is looking at is no longer drawn at the position `@/pixi/layout`
 * authored it. That is invisible to everything here: `toDesignSpace()` subtracts
 * exactly the offset the play plane was drawn with, so `pileAt()` keeps working
 * in untouched design coordinates. Two orderings are load-bearing and are marked
 * where they happen — `updateParallax(dt)` runs before `flushPointer()`, so a
 * click is tested against the offset this frame will be drawn with, and the
 * design point the hit test produced is fed straight back to the camera (the
 * stage adds its own offset back before normalising, so that is not a loop).
 *
 * ## The clock
 *
 * 180 seconds has to be 180 seconds at 30fps and at 144. The engine is ticked
 * from a fixed-step accumulator, never from a raw frame delta, and the pump is
 * gated on pause. A hidden tab stops the app's ticker outright, so it stops
 * here too.
 */

import { showArrival } from '@/chest/arrival-state';
import { chestStore, clearChests, refreshChests, refreshInventory, registerChestOpener, setInventory, settleOpened } from '@/chest/chests-state';
import { devArrival } from '@/chest/dev';
import { dismissSignup, openSignOutConfirm, signOutVerdict, signupStore } from '@/chest/signup-state';
import { bus } from '@/core/bus';
import { GameEngine } from '@/game/engine';
import { forgetLiveRound, recallLiveRound, rememberLiveRound } from '@/net/live-round';
import { RemoteEngine } from '@/net/remote';
import { checkSavedRound, dealOrPickUp, forgetsRound, resumeAfterRefresh, watchCapMs, watchRound } from '@/net/round-claim';
import { isBoard } from '@/ui/leaderboard-data';
import { createStartUi } from '@/ui/start-ui';
import { RoundNotOpened, guardStart, launchStart, whenReady } from '@/net/round-start';
import type { StakeFlight } from '@/ui/money-flight';
import { DEMO_BEATS, DEMO_DECK } from '@/ui/guideScript';
import { isDemoStock, showGoFlash } from '@/ui/go-flash';
import { UNAVAILABLE_NOTE } from '@/ui/results';
import {
  PILE_COUNT,
  ROUND_SECONDS,
  WILD_CARDS,
  type CardId,
  type FinalScore,
  type GameSnapshot,
  type Resolution,
} from '@/game/types';
import { CARD, PILES, STOCK, pileCardPosition, pilePosition } from '@/pixi/layout';
import { createDeck } from '@/game/deck';
import {
  SOLO,
  joinLobbyMatch,
  matchFromView,
  openLobbyMatch,
  stakedMatch,
  takeAsyncMatch,
  type Match,
} from '@/net/match';
import { createChangedSignal, createLatestGate, jobsFor, loadClock, watchForeground } from '@/net/challenges-changed';
import { ownWagersOpen, refreshOwnWagers } from '@/ui/own-wagers-sheet';
import {
  challengeView,
  createChallenge,
  entryFees,
  logout,
  myChallenges,
  openChest,
  openQueue,
  bestScores,
  myRewards,
  inventory,
  myChallengeCounts,
  equipCosmetic,
  financeOf,
  playAsGuest,
  profileOf,
  queueCounts,
  takeChallenge,
  takeChallengeByCode,
  topScores,
  whoAmI,
  type Account,
  type QueueTier,
  type ChallengeView,
  type StakedDeal,
} from '@/net/api';
import { DEFAULT_BOUNDS, saneBounds, type FeeBounds } from '@/ui/fee';
import { registerWorker } from '@/net/notify';
import { planCeremony } from '@/ceremony/plan';
import { CHESTS_ENABLED } from '@/core/flags';
import type { QualityChoice } from '@/ui/settings';
import {
  clearLobbyFromLocation,
  dropLegacyPlayerId,
  legacyPlayerId,
  lobbyFromLocation,
} from '@/net/session';
import { gameOverHold } from '@/motion/pacing';
import { createApp, type PixiApp } from '@/pixi/app';
import { QualityGovernor } from '@/pixi/quality';
import { createTable, type Table } from '@/pixi/table';
import { createAtmosphere, type Atmosphere } from '@/pixi/atmosphere';
import { CardPool, isWildCard, updateCards, type CardSprite } from '@/pixi/card';
import { preloadCardTextures } from '@/pixi/cardTexture';
import { createPileTotals, type PileTotals } from '@/pixi/totals';
import { Choreographer, DEAL_VISIBLE, type ResolveKind } from '@/motion/choreography';
import { VFX } from '@/vfx';
import { hitStopAt, settleAt } from '@/vfx/streakLadder';
import { shareChallengeMe, shareResult, type ShareInput } from '@/share/share';
import { challengeMeCard, profileFromPath, wantsChallengeCard } from '@/ui/challenge-me';
import { mountUI, type UI, type UIHandlers, type UIScreen } from '@/ui';
import { showSharePreview } from '@/ui/share-dialog';
import { audio } from '@/audio';

/* ------------------------------------------------------------------ *
 * Tuning
 * ------------------------------------------------------------------ */

/** Fixed simulation step for the round clock. */
const CLOCK_STEP = 1 / 60;
/** Ceiling on banked time, so one long stall cannot burn the clock down. */
const MAX_CLOCK_CATCHUP = 0.5;

/**
 * Half-width of a pile's hit region in design units. Generous around the card,
 * but always short of the midpoint between two piles — a mis-drop is a lost
 * bonus, so neighbouring regions must never touch.
 */
function hitHalfX(): number {
  // Generous, but never far enough to swallow a neighbouring pile...
  let half = Math.min(CARD.width / 2 + 44, PILES.spacing / 2 - 8);

  // ...and never far enough to reach the stock. In portrait the stock sits in
  // the channel between the two columns, close enough that the default 147
  // would put 24 units of "left pile" on top of it — a tap meant for nothing
  // would play a card.
  let nearest = Infinity;
  for (let i = 0; i < PILE_COUNT; i++) {
    nearest = Math.min(nearest, Math.abs(pilePosition(i).x - STOCK.x));
  }
  if (Number.isFinite(nearest)) {
    half = Math.min(half, Math.max(CARD.width / 2, nearest - CARD.width / 2 - 4));
  }
  return half;
}
/** Vertical slack above and below the fan. */
const HIT_PAD = 26;

/** Dimming applied per card buried under another, and its floor. */
const SHADE_PER_CARD = 0.11;
const SHADE_FLOOR = 0.66;

/* ------------------------------------------------------------------ *
 * Runtime
 * ------------------------------------------------------------------ */

/** How the choreographer and this file get hold of card sprites. */
interface CardSource {
  get(id: CardId): CardSprite;
  recycle(id: CardId): void;
}

/** Everything built by `boot()`. Absent until the renderer and atlas are up. */
interface Runtime {
  readonly pixi: PixiApp;
  /** Replaced wholesale when the screen changes orientation. */
  table: Table;
  readonly pool: CardPool;
  readonly cards: CardSource;
  readonly choreo: Choreographer;
  readonly vfx: VFX;
  readonly totals: PileTotals;
  /** Replaced wholesale when the screen changes orientation. */
  atmosphere: Atmosphere;
}

/**
 * Hit-stop per outcome: `[seconds, timeScale]`.
 *
 * Amplitude says something loud happened; taking time away says it had mass.
 * The plain landing is deliberately the shortest — two frames at 60Hz — because
 * it is the one that repeats.
 */
const HIT_STOP: Record<ResolveKind, readonly [number, number]> = {
  twentyOne: [0.055, 0.12],
  fiveCard: [0.07, 0.1],
  wild: [0.07, 0.1],
  bust: [0.09, 0.08],
};

/**
 * The plain landing — a card added to a pile that did not resolve.
 *
 * `resolveKind()` falls through to `'wild'` for a non-clearing play, so this
 * has to be chosen on `cleared` rather than on the kind, or every ordinary card
 * would land with a bonus-sized freeze.
 */
const HIT_STOP_LAND: readonly [number, number] = [0.033, 0.35];

/** The last play, kept so an undo can put the card back where it came from. */
interface PlayRecord {
  card: CardId;
  pile: number;
  index: number;
}

let runtime: Runtime | null = null;
let ui: UI | null = null;
/**
 * The round in play.
 *
 * A solo round runs on a local `GameEngine`. A ranked one runs on the server
 * and this is the near end of it — same small surface, except that a play has
 * to be asked for rather than decided here. See `@/net/remote`.
 */
let engine: GameEngine | RemoteEngine | null = null;

/** Latest snapshot from any event — the authority for hit-testing and gating. */
let snapshot: GameSnapshot | null = null;
let lastPlay: PlayRecord | null = null;

/**
 * Bumped whenever the visuals are cut to the current game state. Any animation
 * sequence started under an older generation abandons itself at its next await.
 */
let generation = 0;
/** Number of animation sequences in flight. */
let running = 0;

let clockRunning = false;
let paused = false;
let accumulator = 0;

let booted = false;
let startRequested = false;
/** Dev readout only (the director's `render()`): the running quality governor. */
let governorRef: QualityGovernor | null = null;

/** The sprite currently wearing the wild's hero treatment, if any. */
let wildSprite: CardSprite | null = null;
/** Pile under the pointer, or -1. */
let hoveredPile = -1;

const teardown: Array<() => void> = [];

/* ------------------------------------------------------------------ *
 * Small helpers
 * ------------------------------------------------------------------ */

function isBusy(): boolean {
  return running > 0;
}

/** The round is over and the table is finishing its sentence. */
let pendingGameOver: { final: FinalScore; hold: number } | null = null;

/**
 * What the current round is, and who it is against.
 *
 * Solo until something says otherwise. Every multiplayer path in this file is
 * allowed to fail back to this: a scoreboard that is down must cost the player
 * a ranking, never a game.
 */
let match: Match = SOLO;

/**
 * The start screen's status line and busy flag, with an owner (`createStartUi`): a slow flow that wrote them only
 * clears what it wrote, and every ordinary writer below goes through this so it takes ownership as it writes.
 */
const startUi = createStartUi({
  setStatus: (text, tone) => ui?.setStartStatus(text, tone),
  setBusy: (on) => ui?.setStartBusy(on),
});

/**
 * Advance the wait for the game-over screen. Driven from the frame loop so it
 * is measured in the same time as everything else it is waiting for.
 */
function tickGameOver(dt: number): void {
  const wait = pendingGameOver;
  if (!wait || !ui) return;
  // Nothing is timed until the table is quiet; a long resolve should not eat
  // the hold that exists to let the player read its result.
  if (isBusy()) return;
  wait.hold -= dt;
  if (wait.hold > 0) return;
  pendingGameOver = null;
  ui.showGameOver(wait.final);
  void settleMatch(wait.final);
}

/**
 * Report the round and show the verdict.
 *
 * Fired alongside the breakdown rather than before it: the player reads the
 * payout while this is in flight, so a slow server costs nothing. Solo rounds
 * skip it entirely and show no verdict at all — there was nobody to beat, and a
 * line saying so would be noise on every single-player game.
 */
async function settleMatch(final: FinalScore): Promise<void> {
  const played = match;
  // What the Share button will draw: the round as it stands, refined below
  // once the server says how it went.
  const round: ShareInput = { final, user: null };
  lastRound = round;
  if (played.challengeId === null) {
    ui?.setVerdict(null);
    return;
  }
  // A taker's seat carries no code (nothing to dare with); the card only
  // prints one while the challenge is open, which a taker's never is.
  round.challenge = {
    challengeId: played.challengeId,
    code: played.lobby?.code ?? null,
    entryFee: played.stake?.entryFee,
    status: 'open',
  };

  const seat = { share: played.lobby !== null, role: played.role, challengeId: played.challengeId };
  ui?.setVerdict({ outcome: 'pending' }, seat);

  // Nothing is reported. The server dealt this round and refereed it, so it
  // already knows the score — it recorded it the moment the round ended and
  // sent the standings back with that answer. All that is left is to read them.
  if (final.detailsUnavailable) ui?.announce(UNAVAILABLE_NOTE);
  // The reply that settled the round carried the standings; a round whose reply was lost (a drop at the last
  // card, a clock that ran out while the player was away) has them on the challenge's own record, and the
  // engine keeps asking for them for as long as the server cannot be reached.
  const outcome = engine instanceof RemoteEngine ? await engine.collectResult() : null;
  // A restart while the breakdown was counting up: the verdict belongs to a
  // round that is no longer on screen.
  if (match !== played) return;
  if (outcome === null) {
    // The round was played but the server never confirmed it — the reachable
    // case is a connection that dropped mid-round. Say so rather than showing
    // a verdict that was never agreed.
    ui?.setVerdict({ outcome: 'offline' }, seat);
    return;
  }

  // The XP beat, regardless of how the match itself turned out — a loss still
  // earns, which is most of the point of having progression underneath a
  // competitive mode. Refresh the account too, so the balance pill and any
  // rank shown elsewhere agree with the number that just counted up.
  // Chests this round earned, before the beat starts: the tray needs to know
  // whether its CONTINUE opens one. Empty while the feature is off — the
  // server sends none either.
  ui?.setEarnedChests(CHESTS_ENABLED ? (outcome.chests ?? []) : []);

  if (outcome.xpGained) {
    ui?.showXp(outcome.xpGained);
    if (account) {
      setAccount({
        ...account,
        xp: outcome.xpGained.xpAfter,
        xpLevel: outcome.xpGained.levelAfter,
        rank: outcome.xpGained.rankAfter,
      });
    }
  }

  if (outcome.outcome === 'unopposed' || outcome.opponent === null) {
    ui?.setVerdict({ outcome: 'unopposed', mine: outcome.score }, seat);
    return;
  }
  const payout = outcome.settled?.won ? outcome.settled.payout : undefined;
  const stake = outcome.settled?.stake ?? played.stake?.entryFee;
  ui?.setVerdict(
    {
      outcome: outcome.outcome,
      opponent: outcome.opponent.name,
      theirScore: outcome.opponent.score,
      theirLevel: outcome.opponent.level,
      mine: outcome.score,
      // Only on the result that settled the challenge. The payout to a
      // winner; the stake to both — the plate's stake line reads "+200" on a
      // win and "−100" on a loss, which is what the round cost.
      payout,
      stake,
    },
    seat,
  );
  // A win paid this player: fetch the balance the flight is heading for. The top bar is not on this screen —
  // the coins land in its own pill — but every other screen's is, and the truth has to reach it either way
  // (the flight holds the display, so this can arrive before, during or after the coins).
  if (outcome.outcome === 'win' && (payout ?? 0) > 0) void openSession();
  // Matched and settled: the card carries the outcome, not the dare.
  if (round.challenge && outcome.outcome !== 'draw') {
    round.challenge.status = 'complete';
    round.challenge.outcome = {
      won: outcome.outcome === 'win',
      chain: outcome.outcome === 'win' ? (payout ?? 0) : (stake ?? 0),
      opponent: outcome.opponent.name,
      theirScore: outcome.opponent.score,
    };
  }
}

/** The round the results tray is showing, as the share card wants it. */
let lastRound: ShareInput | null = null;
let sharing = false;

/**
 * Share the round on the tray: the cards are drawn here, the story goes to
 * the share sheet where there is one, the preview dialog where there is not.
 */
async function shareRound(): Promise<void> {
  const round = lastRound;
  if (!round || sharing) return;
  sharing = true;
  ui?.setShareBusy(true);
  try {
    const out = await shareResult({
      ...round,
      user: account
        ? { username: account.username, xpLevel: account.xpLevel, rank: account.rank }
        : null,
    });
    sharing = false;
    ui?.setShareBusy(false);
    if (out.mode === 'preview') await showSharePreview(out);
  } catch {
    // The card could not be drawn at all (no canvas): nothing to show.
    ui?.announce('Could not build the share card');
  } finally {
    sharing = false;
    ui?.setShareBusy(false);
  }
}

/**
 * "Challenge Me": share a SETTLED challenge from its own screen. The card is the settled result card with
 * "CHALLENGE ME" over "SCAN TO TAKE ON <NAME>"; its QR and the link it unfurls from land on the player's
 * profile at its challenge card. Nothing on it points at an open challenge.
 */
let sharingChallenge = false;
async function shareSettledChallenge(view: ChallengeView): Promise<void> {
  if (sharingChallenge || !account) return;
  const card = challengeMeCard(view);
  if (!card) return;
  sharingChallenge = true;
  ui?.setChallengeShareBusy(true);
  try {
    const out = await shareChallengeMe({
      final: card.final,
      user: { username: account.username, xpLevel: account.xpLevel, rank: account.rank },
      challengeId: view.id,
      entryFee: view.entryFee,
      outcome: card.outcome,
    });
    sharingChallenge = false;
    ui?.setChallengeShareBusy(false);
    if (out.mode === 'preview') await showSharePreview({ ...out, heading: 'Share your result' });
  } catch {
    ui?.announce('Could not build the share card');
  } finally {
    sharingChallenge = false;
    ui?.setChallengeShareBusy(false);
  }
}

function resolveKind(resolution: Resolution): ResolveKind {
  // One headline outcome, even when bonuses stack: the movement has to read as
  // a single event. Order matches the payout, loudest first.
  if (resolution.isBust) return 'bust';
  if (resolution.isFiveCard) return 'fiveCard';
  if (resolution.is21) return 'twentyOne';
  return 'wild';
}

/**
 * Run an animation sequence. `alive()` goes false once the visuals have been cut
 * to a newer game state, which is the signal to abandon the rest of the
 * sequence rather than animate a board that no longer exists.
 */
function sequence(task: (alive: () => boolean) => Promise<void>): void {
  const mine = generation;
  running++;
  void task(() => mine === generation)
    .catch((error: unknown) => {
      console.error('[main] animation sequence failed', error);
    })
    .finally(() => {
      running--;
    });
}

/* ------------------------------------------------------------------ *
 * Board <-> snapshot
 * ------------------------------------------------------------------ */

/**
 * Cards buried in a fan get a little less light. The top card of a pile is the
 * one the player is reading; the ones under it should recede.
 */
function shadePiles(rt: Runtime, snap: GameSnapshot): void {
  for (const pile of snap.piles) {
    const top = pile.length - 1;
    for (let n = 0; n < pile.length; n++) {
      const id = pile[n];
      if (id === undefined) continue;
      rt.cards.get(id).setShade(Math.max(SHADE_FLOOR, 1 - (top - n) * SHADE_PER_CARD));
    }
  }
}

/**
 * Re-seat the face-down stock. Only the top `DEAL_VISIBLE` cards get sprites —
 * the depth of a 52-card deck is implied by the stack, not modelled by it — so
 * every play promotes one more card into the visible window.
 */
function restack(rt: Runtime, snap: GameSnapshot): void {
  const visible = snap.stock.slice(0, DEAL_VISIBLE);
  for (let depth = 0; depth < visible.length; depth++) {
    const id = visible[depth];
    if (id !== undefined) rt.choreo.placeInStock(id, depth, false);
  }
}

/** Rebuild the whole board from a snapshot with no animation at all. */
function placeBoard(rt: Runtime, snap: GameSnapshot): void {
  for (let pile = 0; pile < snap.piles.length; pile++) {
    const cards = snap.piles[pile] ?? [];
    for (let n = 0; n < cards.length; n++) {
      const id = cards[n];
      if (id !== undefined) rt.choreo.placeInPile(id, pile, n);
    }
  }

  const visible = snap.stock.slice(0, DEAL_VISIBLE);
  for (let depth = 0; depth < visible.length; depth++) {
    const id = visible[depth];
    if (id !== undefined) rt.choreo.placeInStock(id, depth, depth === 0);
  }

  shadePiles(rt, snap);
}

/**
 * Abandon whatever is animating and snap the table to the engine's current
 * state in one frame. This is what keeps a fast player fast: they never wait for
 * a flourish, they just see the board catch up.
 */
/** Pending top-rung slow ramp, if one is waiting for a freeze to release. */
let settleTimer: ReturnType<typeof setTimeout> | null = null;

/** Drop any pending ramp. Returns null, so callers can assign through it. */
function clearSettle(): null {
  if (settleTimer !== null) clearTimeout(settleTimer);
  return null;
}

function cutToState(): void {
  const rt = runtime;
  if (!rt || !engine) return;

  generation++;
  rt.choreo.fastForward();
  // The board is about to be rebuilt under it, so no blur may survive the cut.
  rt.pixi.postfx.clearFocus();
  wildSprite = null;
  // Released and re-acquired rather than reused: `acquire()` resets a sprite
  // unconditionally, which is the only guarantee that a mesh that was J♠ does
  // not come back still wearing its foil.
  rt.pool.releaseAll();

  const snap = engine.snapshot();
  placeBoard(rt, snap);
  syncWild(rt, snap);

  if (snap.phase !== 'over') clockRunning = true;
}

/* ------------------------------------------------------------------ *
 * The wild
 * ------------------------------------------------------------------ */

/**
 * J♠ and J♣ are the only cards that can rescue a busted pile, so mistaking one
 * for J♥ is a gameplay error rather than a cosmetic one. While a wild is the
 * face-up active card it wears the hero foil and all four wells breathe violet.
 */
function syncWild(rt: Runtime, snap: GameSnapshot): void {
  const active = snap.activeCard;
  const wild = snap.phase !== 'over' && active !== null && isWildCard(active);

  if (!wild) {
    clearWild(rt);
    return;
  }

  if (wildSprite !== null && wildSprite.cardId !== active) {
    if (wildSprite.isWild) wildSprite.setWildActive(false);
    wildSprite = null;
  }

  if (active !== null) {
    const sprite = rt.cards.get(active);
    sprite.setWildActive(true);
    wildSprite = sprite;
  }

  // Idempotent by design, so re-asserting it from every snapshot does not
  // restart the staggered wave across the four wells.
  rt.table.setWildAlert(true);
}

/** Drop the hero treatment and the four-well alert. */
function clearWild(rt: Runtime): void {
  if (wildSprite !== null) {
    // A recycled sprite has already lost its foil; the guard only keeps the
    // dev-build warning about non-wilds quiet.
    if (wildSprite.isWild) wildSprite.setWildActive(false);
    wildSprite = null;
  }
  rt.table.setWildAlert(false);
}

/* ------------------------------------------------------------------ *
 * Play sequencing
 * ------------------------------------------------------------------ */

/**
 * Two plays closer together than this are one intent, not two. A deliberate
 * fast click is ~120ms apart at the very quickest, so this is imperceptible to
 * a player trying to go fast while still swallowing a bouncing input or a
 * duplicated event.
 */
const MIN_PLAY_INTERVAL_MS = 70;

/**
 * How deep the backlog has to get before we stop overlapping and cut.
 *
 * The choreographer already turns concurrency into speed rather than a queue
 * (see `#timeScale`), so one card still flying when the next is played is
 * normal fast play, not a desync — overlapping them and letting both
 * accelerate looks right. Cutting there instead teleported the in-flight card
 * into its pile, which is what made fast clicking look like the game was
 * issuing several cards at once.
 */
const CUT_AFTER_CONCURRENT = 3;

let lastPlayAt = Number.NEGATIVE_INFINITY;

/**
 * Called on the frame a played card touches the felt. Installed by the capture
 * director and null in every other build; see `installDirector`.
 */
let onCardLanded: ((pile: number, card: CardId) => void) | null = null;

async function playPile(pileIndex: number): Promise<void> {
  const rt = runtime;
  if (!rt || !engine || !ui) return;

  if (paused || ui.screen !== 'game' || engine.phase !== 'playing') {
    void rt.choreo.nudgePile(pileIndex, 'reject');
    return;
  }
  if (engine.snapshot().activeCard === null) {
    void rt.choreo.nudgePile(pileIndex, 'reject');
    return;
  }

  // Swallowed silently: a bounce is not a rejected play, so it gets no feedback.
  const now = performance.now();
  if (now - lastPlayAt < MIN_PLAY_INTERVAL_MS) return;
  lastPlayAt = now;

  // The opening flourish is still running.
  //
  // `openRound` captured the board as it was at `game:start` and is still
  // working through it — dealing that stock list and then flipping *that*
  // active card. Neither knows about a play made since. The visible symptom is
  // that the card the player just placed gets dragged back to the stock by the
  // opening flip, so the pile's total badge shows a card that is not on the
  // table: the engine counted the play and the board silently disagreed.
  //
  // Cutting first abandons that sequence (via the generation counter) and
  // rebuilds the board from the engine, which is exactly what `cutToState`
  // exists for. `clockRunning` is the flag the opening sets when it finishes,
  // so it doubles as "is the opening still in flight".
  //
  // Only cut for backlog once it is genuinely deep. Below that, overlap and let
  // the choreographer's pressure gain carry the speed.
  if (!clockRunning || running >= CUT_AFTER_CONCURRENT) cutToState();

  // Awaited, but no longer waiting on anything. A solo play resolves in the
  // same tick; a ranked one resolves against the same rules locally and tells
  // the server behind the player's back. See `RemoteEngine`.
  const resolution = await engine.playOn(pileIndex);
  if (!resolution) {
    void rt.choreo.nudgePile(pileIndex, 'reject');
    return;
  }

  // The played card is the last of `cards`, which is also its index in the fan.
  const indexInPile = resolution.cards.length - 1;
  lastPlay = { card: resolution.card, pile: resolution.pile, index: indexInPile };

  const after = engine.snapshot();
  // The stock closes up the moment the card leaves it, not when it lands.
  restack(rt, after);
  // The wild the player just spent stops being the hero here. Whether the *next*
  // card is one is not announced until it has been turned over — lighting the
  // wells over a face-down card would give the hand away.
  clearWild(rt);

  const kind = resolveKind(resolution);

  sequence(async (alive) => {
    await rt.choreo.playCardToPile(
      resolution.card,
      resolution.pile,
      indexInPile,
      resolution.cards,
    );
    if (!alive()) return;

    // The felt answers the landing. `playCardToPile` resolves the moment the
    // card is down rather than when its settle finishes, so this is contact,
    // and the point is the one layout.ts sent the card to.
    const landed = pileCardPosition(resolution.pile, indexInPile);
    rt.table.impact(landed.x, landed.y);

    // The exact frame a card touches the felt, for the trailer capture rig.
    // It matters that this is *here* rather than at `pile:resolve`: the engine
    // resolves the instant the pile is tapped, and the card is still in the air
    // for the length of a deal after that. A sound effect placed on the engine
    // event lands a fifth of a second before the impact it is meant to be, which
    // is exactly far enough to be heard as wrong. `import.meta.env.DEV` is a
    // literal Vite replaces at build time, so the production bundle has neither
    // the call nor the variable.
    if (import.meta.env.DEV) onCardLanded?.(resolution.pile, resolution.card);

    // Hit-stop, scaled to what just happened.
    //
    // The ordinary landing gets two frames — below conscious perception, and it
    // fires hundreds of times a round, so it is where the cumulative sense of
    // weight actually comes from. A bust gets a real freeze, because it is the
    // one moment that should feel like a mistake.
    rt.choreo.hitStop(...(resolution.cleared ? HIT_STOP[kind] : HIT_STOP_LAND));

    // And the rung's own freeze on top of it — the one streak channel that
    // costs no screen area at all. Its numbers and the reasoning are in
    // `@/vfx/streakLadder.hitStopAt`, with the rest of the ladder's per-rung
    // response, because that is what they are.
    //
    // Called *second* on purpose. `hitStop` keeps the strongest factor already
    // in flight and always takes the latest duration, so a play that both
    // clears and streaks ends up with the clear's depth for the streak's
    // length — which is the right answer for an event that is genuinely both.
    if (resolution.streakLevel > 0) rt.choreo.hitStop(...hitStopAt(resolution.streakLevel));

    // The top rungs ease out of that freeze instead of snapping out of it.
    //
    // Scheduled rather than tweened because `hitStop` owns the time scale and
    // will not let a weaker factor shorten a freeze in flight — so the ramp has
    // to arrive *after* the freeze has released, which is exactly what this
    // waits for. See `settleAt` for why only the top two rungs get one.
    settleTimer = clearSettle();
    const settle = settleAt(resolution.streakLevel);
    if (settle) {
      const [freeze] = hitStopAt(resolution.streakLevel);
      const era = generation;
      settleTimer = setTimeout(
        () => {
          settleTimer = null;
          // A cut, a restart or the round ending abandons it: coming out of a
          // slow ramp onto a board that has been rebuilt underneath would be a
          // stutter with no cause on screen.
          if (era !== generation || !runtime) return;
          runtime.choreo.hitStop(...settle);
        },
        freeze * 1000 + 8,
      );
    }

    // Everything that explains the play fires on contact: the felt washes with
    // the outcome colour, the burst and the numbers go up, then the pile leaves.
    rt.vfx.resolve(resolution);

    if (resolution.cleared) {
      // Shallow defocus on the payoff: everything except this pile eases into a
      // 1-2px blur and back out. Gated on `cleared`, so it can never soften a
      // card the player is still choosing against, and it releases itself.
      rt.pixi.postfx.focusPile(resolution.pile, resolution.cards.length);
      rt.table.pulsePile(resolution.pile, kind);
      await rt.choreo.resolvePile(resolution.pile, kind, resolution.cards);
      if (!alive()) return;
    }

    // A ranked play resolves locally and animates immediately; the card that
    // follows it is the one thing only the server knows. This is where that
    // debt comes due, and it is the right place for it — the throw and the
    // resolve above have already run several times longer than the round trip,
    // so on any ordinary connection this has nothing left to wait for.
    if (engine instanceof RemoteEngine) {
      // The stock as it stands: a face-down placeholder on top, standing in for
      // a card this end has not been told yet.
      const standins = engine.snapshot().stock.slice(0, DEAL_VISIBLE);
      const live = engine;
      await live.settled();
      if (!alive()) {
        // The board was cut to a state while this was waiting, and the cut drew the stock face down: the card
        // that has just arrived would never be turned over. Draw it now, unless the round has moved on.
        if (engine === live && live.snapshot().activeCard !== null) cutToState();
        return;
      }

      // The placeholder that was covering the top of the deck has no slot any
      // more — the real card takes it. Left alone it stays parked at slot 0,
      // face down, over the card the player is meant to be reading.
      const revealed = engine.snapshot();
      const kept = new Set(revealed.stock);
      rt.choreo.hide(standins.filter((id) => !kept.has(id)));
      restack(rt, revealed);
    }

    const settled = engine?.snapshot() ?? null;
    if (settled === null) return;
    shadePiles(rt, settled);

    if (settled.phase !== 'over' && settled.activeCard !== null) {
      await rt.choreo.flipStockCard(settled.activeCard);
      if (!alive()) return;
      syncWild(rt, engine?.snapshot() ?? settled);
    }
  });
}

async function undoPlay(): Promise<void> {
  if (!engine) return;
  // The round is over and the table is still showing what ended it. The game
  // screen is deliberately still up during that beat, which leaves UNDO
  // reachable for a second or so after the last card — and taking a play back
  // now would rewind into a finished round and then drop a breakdown computed
  // from the state before the rewind. The round is done; there is nothing left
  // to take back.
  if (pendingGameOver) return;
  if (isBusy()) cutToState();
  await engine.undo();
}

/* ------------------------------------------------------------------ *
 * The first-run walkthrough
 * ------------------------------------------------------------------ */

/** Bumped to abandon a walkthrough in flight. */
let guideRun = 0;

/**
 * Play the walkthrough round.
 *
 * This is not a diagram of the game, it *is* the game: a stacked deck handed to
 * the ordinary engine, dealt onto the ordinary board, thrown by the ordinary
 * choreography. The wild wears its foil because it is the wild; the streak
 * border flashes because a streak really opened; the wells are the right size
 * because nothing here drew any wells. A copy could never have managed it.
 *
 * Every card is placed through `playPile`, the same function a tap reaches.
 *
 * The pacing waits for the board rather than running on a stopwatch. An earlier
 * version scheduled every beat off one clock, and since a pile clearing takes
 * as long as it takes — longer on a slow device — the captions ran ahead of the
 * cards they were describing. Waiting for the choreographer to go quiet paces
 * it correctly on hardware nobody has tested.
 */
async function runGuide(): Promise<void> {
  const mine = ++guideRun;

  // The start screen is up long before the renderer is, and pressing Play the
  // moment it appears is exactly what an impatient first-timer does. Wait for a
  // board to play on rather than returning and leaving the walkthrough showing
  // captions over nothing — which is what it did.
  const until = performance.now() + 15_000;
  while (runtime === null && mine === guideRun && performance.now() < until) await pause(100);

  const rt = runtime;
  if (!rt || mine !== guideRun) return;
  const live = (): boolean => mine === guideRun;

  // The walkthrough is a solo round — nothing it does may settle against a
  // challenge — but the match it displaces is restored when it ends. It used
  // to leave SOLO in force, and the staked round queued behind it (the guide
  // runs before a first-timer's first hand) was dealt unranked: the stake
  // gone, the deck the wrong one, the challenge still reading YOUR TURN.
  const displaced = match;
  match = SOLO;
  try {
    await runGuideBody(rt, live);
  } finally {
    // Only if nothing set a match meanwhile: the take paths set their own
    // the moment the guide hands over, and that one wins.
    if (match === SOLO) match = displaced;
  }
}

async function runGuideBody(rt: Runtime, live: () => boolean): Promise<void> {
  resetBoard(rt);

  // Move the felt up out of the narration before anything is dealt, so no card
  // ever lands behind the sentence describing it. Once, here, rather than
  // tracked as the caption changes — a felt that re-fits mid-play takes the
  // play sequence with it.
  // Heading first, then measure: the felt should be fitted to the panel's final
  // height, not re-fitted when it shrinks.
  ui?.compactGuide();
  rt.pixi.setGuideInset(ui?.guideInset() ?? 0);

  // The ordinary round length. It was ten minutes at first so the walkthrough
  // could not be cut short, which put 10:00 on the clock — teaching a first
  // timer the one number about this game that is never true. Every round is
  // three minutes, so the demo runs on three minutes; it takes about thirty
  // seconds, and the loop below bails if a player leaves it up long enough for
  // the round to actually run out.
  if (engine instanceof RemoteEngine) engine.dispose();
  engine = new GameEngine(bus, { deck: DEMO_DECK });
  engine.start();

  // Let the opening flourish land before the first card is placed.
  await pause(1500);

  for (const beat of DEMO_BEATS) {
    if (!live()) return;
    // Left open long enough for the clock to run out — hand them their round
    // rather than dropping a breakdown for a game they never played.
    if (engine === null || engine.isOver) {
      pendingGameOver = null;
      ui?.endGuide();
      return;
    }
    if (beat.caption !== null) ui?.setGuideCaption(beat.caption);
    await playPile(beat.pile);
    if (!live()) return;
    // The play, then whatever it set off — a clear, a burst, the streak border.
    await waitForBoard();
    if (!live()) return;
    await pause(beat.hold * 1000);
  }

  if (!live()) return;
  await pause(900);
  if (live()) ui?.endGuide();
}

/** Cut the walkthrough off — skipped, finished, or interrupted by a restart. */
function stopGuide(): void {
  guideRun++;
  runtime?.pixi.setGuideInset(0);
}

function pause(ms: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

/** Resolve once nothing is moving on the table, or after a sensible ceiling. */
async function waitForBoard(): Promise<void> {
  const until = performance.now() + 4000;
  while (isBusy() && performance.now() < until) await pause(60);
}

/* ------------------------------------------------------------------ *
 * Round lifecycle
 * ------------------------------------------------------------------ */

/**
 * Put the board back to nothing, ready for a deck to be dealt onto it.
 *
 * Shared by every way a round can begin — the start screen, the walkthrough and
 * the capture director — because a reset that is only *nearly* the same in
 * three places is one that will drift in one of them.
 *
 * The order is the interesting part. `fastForward` lands every animation
 * *before* `releaseAll` hands the sprites back, because a timeline finalising
 * onto a recycled card writes to something that is no longer on the table.
 */
function resetBoard(rt: Runtime): void {
  generation++;
  // Every game opens on the governor's start level, judged from its own frames: a tier spent in the
  // last game must not be what the first seconds of this one are drawn at.
  governorRef?.reset();
  // Restarting from the game-over screen, or straight off a hotkey, must not
  // leave a breakdown queued to drop over the new round.
  pendingGameOver = null;
  rt.choreo.fastForward();
  rt.pixi.postfx.clearFocus();
  wildSprite = null;
  rt.pool.releaseAll();
  rt.table.setWildAlert(false);
  clearHover(rt);
  for (let i = 0; i < PILE_COUNT; i++) rt.table.setPileTilt(i, 0, 0);

  lastPlay = null;
  clockRunning = false;
  accumulator = 0;
  paused = false;
  ui?.hud.setReconnecting(false);

  audio.init();
  void audio.unlock();
}

/** The copy code for why a paid round did not open: the server's words, or the generic. */
function notOpenedCode(
  reason: string,
): 'round-already-played' | 'round-finished' | 'round-closed' | 'round-not-seated' | 'round-not-started' {
  const r = reason.toLowerCase();
  if (r.includes('already played')) return 'round-already-played';
  // The server's `not-seated` (403): a staked challenge this account never took. Nothing was charged or seated.
  if (r.includes('not-seated')) return 'round-not-seated';
  // The server's `challenge-closed`: resolved (settled, refunded, voided, forfeited) or past its deadline.
  if (r.includes('closed')) return 'round-closed';
  if (r.includes('finished')) return 'round-finished';
  return 'round-not-started';
}

/**
 * Start a round nobody is guarding. One that could not open puts the player back at the start screen with the
 * reason, instead of leaving a reset table and a dead Play button.
 */
function startUnguarded(): void {
  launchStart(startRound, (reason) => {
    ui?.showStart();
    startUi.status(`Could not start — ${reason}. Try again.`, 'rose');
  });
}

async function startRound(): Promise<void> {
  const rt = runtime;
  if (!rt) {
    startRequested = true;
    return;
  }

  // Nothing the walkthrough had queued may land on the player's round.
  stopGuide();

  // Which round the player is in, from this moment: a slower answer to anything older (a ranked deal still in
  // flight, a boot-time resume) finds this changed and stands down.
  const mine = ++roundToken;
  resetBoard(rt);
  // The round being left (a restart before it ended) has nothing more to say to the server.
  if (engine instanceof RemoteEngine) engine.dispose();

  // A solo round is dealt here. A ranked one is not: the deck belongs to the
  // server, which deals it a card at a time and never says what is coming — so
  // there is nothing to seed and nothing to replay locally.
  if (match.challengeId === null) {
    engine = new GameEngine(bus);
    engine.start();
    return;
  }

  const challengeId = match.challengeId;
  // A live round on another challenge (the saved key is one slot, and saving this one over it would orphan it) is
  // picked up first: a deep link or a second take must not lose a round that is still being played.
  const saved = await checkSavedRound({
    saved: recallLiveRound(),
    challengeId,
    resume: (other) => RemoteEngine.resume(bus, { challengeId: other }),
    forget: forgetLiveRound,
    current: () => mine === roundToken,
  });
  if (saved.kind === 'stale') return;
  if (saved.kind === 'resume') {
    beginResumed(rt, saved.engine);
    ui?.announce('Your other round is still running. This one is waiting for you on its challenge page.');
    return;
  }
  if (saved.kind === 'unknown') {
    // Every kind of round, not only a paid one: returning here would leave the board reset, no engine and nothing
    // said. The thrower's caller reports it (`guardStart` for a seat, `startUnguarded` for the rest).
    throw new RoundNotOpened('could not check the round in progress');
  }
  // Remembered BEFORE asking: the server claims the seat and deals the round before it replies, so a reply that
  // is lost, or a page that is reloaded while it is in flight, leaves a live round this tab must be able to
  // find again. Forgotten below only when the server says, definitely, that there is none.
  rememberLiveRound(challengeId);
  // No player id goes with this: the session is the identity, and a take is charged and seated under it.
  const dealt = await dealOrPickUp({
    open: () => RemoteEngine.open(bus, { challengeId }),
    resume: () => RemoteEngine.resume(bus, { challengeId }),
    forget: forgetLiveRound,
    // Restarted, or sent home, while the server was answering: that round is nobody's now.
    current: () => mine === roundToken,
  });
  if (dealt.kind === 'stale') return;

  if (dealt.kind === 'refused' && match.kind === 'staked') {
    // The deal may still have happened (a reply lost, a lookup lost): keep looking for the round in the
    // background, until it turns up, the server says there is none, or the player starts something else.
    void watchForRound(rt, challengeId, mine);
    // A seat that was PAID for is not a game to fall back from. Playing it locally would spend the round
    // on a table the server never sees — no result, no verdict, no payout — and leave the stake gone with
    // nothing to show. Say so; the seat is still there to be played.
    throw new RoundNotOpened(dealt.error);
  }

  if (dealt.kind === 'refused') {
    // The scoreboard being unreachable is not a reason to refuse to deal cards.
    // The round is played locally and simply is not recorded — which is the
    // same promise solo play makes, said out loud.
    startUi.status(`Playing unranked — ${dealt.error}`, 'rose');
    match = SOLO;
    engine = new GameEngine(bus);
    engine.start();
    return;
  }

  if (dealt.kind === 'picked-up') {
    // The reply was lost but the round was there: it is picked up where it is, not dealt again.
    beginResumed(rt, dealt.engine);
    return;
  }
  engine = dealt.engine;
  attachRemote(dealt.engine, challengeId);
  engine.start();
}

/**
 * What the table does for a ranked round, whether it was just dealt or just picked up again.
 *
 * The prediction is the same two pure functions the server runs, on inputs the player can see, so it agrees with
 * the answer for every play that is legal when it arrives. The exception is a play made as the clock expires:
 * legal here, refused there. `onDiverge` rebuilds the table rather than leave a board that only this end
 * believes in; it is also what a resync from the server's own account of the round comes through.
 */
function attachRemote(live: RemoteEngine, challengeId: string): void {
  live.onDiverge = () => cutToState();
  // 'Reconnecting…' while an answer the round is owed has not come; the clock behind it keeps running.
  live.onReconnecting = (on) => ui?.hud.setReconnecting(on);
  // Forgotten only when there is nothing to come back to: another driver has the round (it is not this tab's to
  // reclaim), or the server has no such round. A sign-in that lapsed, or a refusal that may be passing (a deploy),
  // keeps the key: a refresh finds the round again.
  live.onLost = (why) => void roundLeft(live, challengeId, LOST_NOTICE, forgetsRound(why));
  live.onTakenOver = () => void roundLeft(live, challengeId, TAKEN_OVER_NOTICE, forgetsRound('taken-over'));
}

const LOST_NOTICE = 'Your round was interrupted and could not be reached again. If it was recorded, its result will show here.';
const TAKEN_OVER_NOTICE = 'This round is being played in another tab or device.';

/**
 * This table is no longer the round's (the server dropped it, the sign-in is gone, or another tab or device of the
 * same player took it over). It is not left frozen and it does not retry: it goes to the challenge's own page,
 * which shows the verdict if the round was settled, with a notice saying why. A round another driver now has is not
 * this tab's to come back to, so a refresh here does not reclaim it.
 */
async function roundLeft(left: RemoteEngine, challengeId: string, notice: string, forget: boolean): Promise<void> {
  if (engine !== left) return;
  if (forget) forgetLiveRound();
  left.dispose();
  pendingGameOver = null;
  clockRunning = false;
  ui?.hud.setReconnecting(false);
  await openChallenge(challengeId);
  // Only if nothing else has taken the screen while the page loaded.
  if (engine === left) ui?.setChallengeNotice(notice);
  ui?.announce(notice);
}

/** The table, rebuilt around a round that is already in progress: placed, not dealt, no GO. */
function beginResumed(rt: Runtime, resumed: RemoteEngine, record?: ChallengeView): void {
  stopGuide();
  resetBoard(rt);
  const challengeId = resumed.challengeId;
  // What the round is, from the server's record if it came; the match already in force if this is the same
  // round (a deal whose reply was lost); a bare ranked seat if all that is known is the challenge.
  match = record ? matchFromView(record) : match.challengeId === challengeId ? match : { ...SOLO, kind: 'staked', challengeId };
  engine = resumed;
  attachRemote(resumed, challengeId);
  // From here on this tab drives it: its first sync claims the round, and any other tab of the player's stands down.
  rememberLiveRound(challengeId);
  // `openRound` places the board as it stands instead of dealing it: this is not a start.
  resuming = true;
  resumed.start();
  // The game:start the HUD just reset itself from describes a mid-round board; its readouts are the round's.
  const { seconds, cards } = resumed.roundSize;
  ui?.hud.reset({ roundSeconds: seconds, totalCards: cards });
  ui?.hud.sync(resumed.snapshot());
  // (`resumed.start()` above has already claimed the round: a resumed engine syncs as it starts.)
}

/**
 * Keep looking for a round whose deal could not be confirmed, quietly, and pick it up if it turns up.
 *
 * It stands down when the player starts anything (`roundToken`), when they leave the screen they were on, and
 * after the longest a round can live (its length plus the server's idle TTL: past it there is nothing to find). It
 * forgets the saved round only on the server's "no live round for you", and never touches the start screen. And it
 * never yanks the player into a round from another screen: if the round turns up after they have moved on, they
 * are told it is still running and given a button, not taken there.
 */
/** How long the watcher lets the failed start's own screen change settle before it counts a change of screen as the player leaving. */
const WATCH_SETTLE_MS = 3000;

async function watchForRound(rt: Runtime, challengeId: string, mine: number): Promise<void> {
  // The screen the player is on once the failed start has settled (it lands them on the challenge page or back
  // at the start screen, a moment after this begins): leaving THAT is leaving. Until then, anywhere is fine.
  let screen: string | undefined;
  const settleScreen = globalThis.setTimeout(() => (screen = ui?.screen), WATCH_SETTLE_MS);
  const onScreen = (): boolean => screen === undefined || ui?.screen === screen;
  const found = await watchRound({
    resume: () => RemoteEngine.resume(bus, { challengeId }),
    forget: forgetLiveRound,
    current: () => mine === roundToken && onScreen(),
    capMs: watchCapMs(),
  });
  globalThis.clearTimeout(settleScreen);
  if (found.kind !== 'found') return;
  const record = await challengeView(challengeId);
  if (mine !== roundToken) {
    found.engine.dispose();
    return;
  }
  if (!onScreen()) {
    // Found, but the player is somewhere else now.
    found.engine.dispose();
    offerResume(rt, challengeId);
    return;
  }
  beginResumed(rt, found.engine, record.ok ? record.value : undefined);
}

let resumeBanner: HTMLElement | null = null;

/** A non-blocking notice that a round is still running, with a Resume button: for a round that turned up while the player was elsewhere. */
function offerResume(rt: Runtime, challengeId: string): void {
  if (resumeBanner) return;
  const el = document.createElement('div');
  el.className = 'w21-renderer-banner';
  el.setAttribute('role', 'status');
  const text = document.createElement('p');
  text.textContent = 'Your round is still running.';
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Resume';
  button.addEventListener('click', () => {
    el.remove();
    resumeBanner = null;
    rememberLiveRound(challengeId);
    void resumeLiveRound(rt);
  });
  el.append(text, button);
  document.body.append(el);
  resumeBanner = el;
}

/**
 * Pick a ranked round back up after a refresh, if this tab was in one (`resumeAfterRefresh` is the policy).
 * Returns true when a round was resumed.
 */
async function resumeLiveRound(rt: Runtime): Promise<boolean> {
  const challengeId = recallLiveRound();
  if (challengeId === null) return false;
  // The start screen's status and busy flag, while this flow still owns them (see `startUi`).
  let owned = startUi.claim();
  const result = await resumeAfterRefresh({
    challengeId,
    // Before anything is awaited: see `RefreshDeps.claim`.
    claim: () => {
      const mine = ++roundToken;
      owned = startUi.claim();
      return () => mine === roundToken;
    },
    resume: () => RemoteEngine.resume(bus, { challengeId }),
    forget: forgetLiveRound,
    status: (text, tone) => owned.status(text, tone),
    busy: (on) => owned.busy(on),
    begin: async (resumed, current) => {
      const record = await challengeView(challengeId);
      if (!current()) return false;
      beginResumed(rt, resumed, record.ok ? record.value : undefined);
      return true;
    },
    // Not live any more: it ran out, or was recorded, while the page was away.
    gone: (error) => {
      if (error === 'no-live-session') void openChallenge(challengeId);
    },
  });
  return result === 'resumed';
}

/** The next `openRound` is a round being picked up again (`resumeLiveRound`), not dealt. */
let resuming = false;

/**
 * Which round the player is actually in.
 *
 * `startRound` waits on the server for a ranked deal, and in that window the
 * player can restart, go home or be sent to the game-over screen. The token
 * makes a late answer to an abandoned round harmless.
 */
let roundToken = 0;

/** The opening: sweep the stock in, then turn the first card over. */
function openRound(rt: Runtime, snap: GameSnapshot): void {
  lastPlay = null;
  clockRunning = false;
  accumulator = 0;

  // Picked up again after a refresh: the board is whatever it was, so it is placed, not dealt. The clock was
  // never stopped (the server's did not wait for this page), and the GO belongs to a round that is starting.
  if (resuming) {
    resuming = false;
    placeBoard(rt, snap);
    syncWild(rt, snap);
    clockRunning = true;
    return;
  }

  // `dealToStock` only turns each sprite visible at its own moment in the
  // stagger, and a freshly acquired sprite sits at the design origin until
  // then. Check them out and hide them first so the corner stays empty.
  for (const id of snap.stock.slice(0, DEAL_VISIBLE)) {
    rt.cards.get(id).visible = false;
  }

  sequence(async (alive) => {
    await rt.choreo.dealToStock(snap.stock);
    if (!alive()) return;
    // The stock lands too. Softer than a played card — this is a deck being set
    // down, not a card thrown at the felt.
    rt.table.impact(STOCK.x, STOCK.y, 0.7);

    if (snap.activeCard !== null) {
      await rt.choreo.flipStockCard(snap.activeCard);
      if (!alive()) return;
    }

    syncWild(rt, engine?.snapshot() ?? snap);

    // A ranked round became playable on the server while this flourish was
    // running — its intro window is deliberately shorter than the flourish, so
    // the player never waits on it. Ask once here rather than letting the frame
    // loop discover it, so the very first tap is accepted.
    if (engine instanceof RemoteEngine) {
      await engine.sync();
      if (!alive()) return;
    }

    // The clock starts when the player can actually act, not when Play is
    // pressed — the opening flourish is not part of their 180 seconds.
    clockRunning = true;

    // The GO marks that moment, as in Blockari. There is no countdown here, so
    // every round dealt fresh gets it; the walkthrough is a scripted demo and
    // does not. (Nothing resumes through this path — a pause never re-deals.)
    if (showGoFlash({ scripted: isDemoStock(snap.stock) })) ui?.hud.flashGo();
  });
}

/** Undo: rebuild the restored board, then carry the card back to the stock. */
function rewind(rt: Runtime, snap: GameSnapshot): void {
  const previous = lastPlay;
  lastPlay = null;

  generation++;
  // Restarting from the game-over screen, or straight off a hotkey, must not
  // leave a breakdown queued to drop over the new round.
  pendingGameOver = null;
  rt.choreo.fastForward();
  rt.pixi.postfx.clearFocus();
  wildSprite = null;
  rt.pool.releaseAll();
  placeBoard(rt, snap);

  if (previous !== null) {
    // Put it back where it was so the return reads as a rewind of the throw.
    rt.choreo.placeInPile(previous.card, previous.pile, previous.index);
    syncWild(rt, snap);
    sequence(async () => {
      await rt.choreo.returnToStock(previous.card);
      // Quieter still: a card being taken back, not played.
      rt.table.impact(STOCK.x, STOCK.y, 0.55);
    });
  } else {
    syncWild(rt, snap);
  }
}

function endRound(rt: Runtime, final: FinalScore): void {
  // Over and recorded: a refresh has nothing to come back to.
  forgetLiveRound();
  clockRunning = false;
  accumulator = 0;
  clearWild(rt);
  // The breakdown is read, not admired: nothing on the table may stay soft.
  rt.pixi.postfx.clearFocus();
  clearHover(rt);

  // Sized to what is actually on screen — see `@/motion/pacing`.
  pendingGameOver = { final, hold: gameOverHold(final.perfectGame, isBusy()) };
}

/* ------------------------------------------------------------------ *
 * Pointer input — design space only
 * ------------------------------------------------------------------ */

/**
 * Which pile a design-space point falls on, or -1.
 *
 * The region grows downward with the fan, because a five-card pile is nearly
 * twice as tall as an empty one and the player aims at the cards they can see.
 */
function pileAt(x: number, y: number): number {
  const snap = snapshot;
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (let i = 0; i < PILE_COUNT; i++) {
    const centre = pilePosition(i);
    const depth = Math.max(0, (snap?.piles[i]?.length ?? 1) - 1);
    const top = centre.y - CARD.height / 2 - HIT_PAD;
    const bottom = centre.y + depth * PILES.fan + CARD.height / 2 + HIT_PAD;
    if (y < top || y > bottom) continue;

    const distance = Math.abs(x - centre.x);
    if (distance > hitHalfX()) continue;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = i;
    }
  }

  return best;
}

/** True when a click would actually place a card. Drives the cursor. */
function canPlayNow(): boolean {
  if (!engine || !ui || paused) return false;
  if (ui.screen !== 'game') return false;
  return engine.phase === 'playing' && engine.snapshot().activeCard !== null;
}

function clearHover(rt: Runtime): void {
  if (hoveredPile < 0) return;
  rt.table.highlightPile(hoveredPile, false);
  rt.table.setPileTilt(hoveredPile, 0, 0);
  void rt.choreo.nudgePile(hoveredPile, 'hoverOut');
  hoveredPile = -1;
  if (rt.pixi.canvas.style.cursor !== 'default') rt.pixi.canvas.style.cursor = 'default';
}

/** Latest pointer position in client px, or null when nothing new arrived. */
let pendingPointer: { x: number; y: number } | null = null;

/**
 * Apply the most recent pointer position, once per frame, immediately before
 * the scene is drawn — so the highlight it produces is visible on that same
 * frame rather than the next one.
 */
function flushPointer(rt: Runtime): void {
  const p = pendingPointer;
  if (!p) return;
  pendingPointer = null;
  const point = rt.pixi.toDesignSpace(p.x, p.y);
  // Aim the camera at the same point the hit test uses. `setParallaxDesign`
  // rather than `setParallax(clientX, clientY)` purely to avoid a second
  // getBoundingClientRect() per frame — the two are otherwise identical, and
  // the stage adds its own offset back before normalising, so feeding it the
  // point it just un-offset is not a feedback loop.
  rt.pixi.stage.setParallaxDesign(point.x, point.y);
  setHover(rt, pileAt(point.x, point.y), point.x, point.y);
}

function setHover(rt: Runtime, pile: number, x: number, y: number): void {
  if (pile !== hoveredPile) {
    if (hoveredPile >= 0) {
      rt.table.highlightPile(hoveredPile, false);
      rt.table.setPileTilt(hoveredPile, 0, 0);
      void rt.choreo.nudgePile(hoveredPile, 'hoverOut');
    }
    hoveredPile = pile;
    if (pile >= 0) {
      // Division of labour: the table lights the well, motion moves it.
      rt.table.highlightPile(pile, true);
      void rt.choreo.nudgePile(pile, 'hover');
      audio.uiHover({ pile });
    }
    rt.pixi.canvas.style.cursor = pile >= 0 && canPlayNow() ? 'pointer' : 'default';
  }

  if (pile >= 0) {
    // A light parallax lean, so the recess tracks the pointer instead of sitting
    // dead under it. The table clamps this to +/-1.
    const centre = pilePosition(pile);
    rt.table.setPileTilt(
      pile,
      (x - centre.x) / hitHalfX(),
      (y - centre.y) / (CARD.height / 2 + HIT_PAD),
    );
  }
}

function installPointer(rt: Runtime): void {
  const canvas = rt.pixi.canvas;

  // Pointer moves are recorded, not acted on. A browser can fire pointermove
  // many times between two frames, and hit-testing plus writing tilt uniforms on
  // every one of them piles main-thread work on precisely when the frame is
  // already struggling — which is when the hover felt laggy. Only the latest
  // position matters, so the work happens once per frame in flushPointer().
  const onMove = (event: PointerEvent): void => {
    pendingPointer = { x: event.clientX, y: event.clientY };
  };

  const onDown = (event: PointerEvent): void => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const point = rt.pixi.toDesignSpace(event.clientX, event.clientY);
    const pile = pileAt(point.x, point.y);
    if (pile < 0) {
      clearHover(rt);
      return;
    }
    event.preventDefault();
    setHover(rt, pile, point.x, point.y);
    void rt.choreo.nudgePile(pile, 'press');
    audio.uiPress({ pile });
    void playPile(pile);
  };

  const onUp = (event: PointerEvent): void => {
    // A finger has no hover state to leave behind, and no position to hold the
    // camera at once it lifts off the glass.
    if (event.pointerType !== 'mouse') {
      rt.pixi.stage.releaseParallax();
      clearHover(rt);
    }
  };

  const onLeave = (): void => {
    // Drop the queued position too, or the next frame re-applies a hover the
    // pointer has already left.
    pendingPointer = null;
    rt.pixi.stage.releaseParallax();
    clearHover(rt);
  };

  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointerleave', onLeave);
  canvas.addEventListener('pointercancel', onLeave);

  teardown.push(() => {
    canvas.removeEventListener('pointermove', onMove);
    canvas.removeEventListener('pointerdown', onDown);
    canvas.removeEventListener('pointerup', onUp);
    canvas.removeEventListener('pointerleave', onLeave);
    canvas.removeEventListener('pointercancel', onLeave);
  });
}

/* ------------------------------------------------------------------ *
 * Bus wiring
 * ------------------------------------------------------------------ */

function installBus(rt: Runtime): void {
  // Every event carries a snapshot; keeping the newest one is what lets the hit
  // test know how tall each pile has grown.
  teardown.push(
    bus.onAny((event) => {
      snapshot = event.snapshot;
    }),
  );

  teardown.push(
    bus.on('game:start', (event) => openRound(rt, event.snapshot)),
    bus.on('undo', (event) => rewind(rt, event.snapshot)),
    bus.on('game:over', (event) => endRound(rt, event.final)),
  );
}

/* ------------------------------------------------------------------ *
 * Boot
 * ------------------------------------------------------------------ */

/**
 * The canvas host. `ui.css` deliberately does not size `#stage`, and a
 * zero-height host is a black screen that looks exactly like a shader bug, so
 * it is pinned to the viewport here — behind the overlay, which owns z-index 10.
 */
function resolveHost(): HTMLElement {
  let host = document.getElementById('stage');
  if (host === null) {
    host = document.createElement('div');
    host.id = 'stage';
    document.body.prepend(host);
  }
  host.style.position = 'fixed';
  host.style.inset = '0';
  host.style.zIndex = '0';
  return host;
}

/**
 * Screens the table is frozen under. Not the game, the guide (which plays on
 * the game screen) or the results, whose ceremony plays on the felt.
 */
const SHELL_SCREENS: ReadonlySet<UIScreen> = new Set<UIScreen>([
  'start',
  'history',
  'create',
  'take',
  'challenge',
  'profile',
  'howitworks',
  'settings',
]);

let shellIdle = false;


/**
 * `?diag=1`: what the GPU is and what the renderer has seen, over the page, for a screenshot from a
 * player's phone. Off unless asked for.
 */
function mountDiagOverlay(pixi: { diagnostics(): string[] }, governor: QualityGovernor): void {
  const errors: string[] = [];
  const keep = (text: string): void => {
    errors.push(text.replace(/\s+/g, ' ').slice(0, 140));
    if (errors.length > 4) errors.shift();
  };
  for (const kind of ['error', 'warn'] as const) {
    const original = console[kind].bind(console);
    console[kind] = (...args: unknown[]): void => {
      keep(args.map((a) => (typeof a === 'string' ? a : a instanceof Error ? a.message : String(a))).join(' '));
      original(...args);
    };
  }
  globalThis.addEventListener('error', (e) => keep(`error: ${e.message}`));
  const el = document.createElement('pre');
  el.style.cssText =
    'position:fixed;left:0;top:0;right:0;z-index:10001;margin:0;padding:6px 8px;pointer-events:none;' +
    'font:10px/1.3 monospace;color:#9f9;background:rgba(0,0,0,0.78);white-space:pre-wrap;word-break:break-all';
  document.body.append(el);
  const paint = (): void => {
    el.textContent = [
      ...pixi.diagnostics(),
      `quality ${governor.pinned ?? 'auto'} ${governor.quality} | ${navigator.userAgent.replace(/^Mozilla\/5.0 /, '').slice(0, 110)}`,
      ...errors.map((e) => `! ${e}`),
    ].join('\n');
  };
  paint();
  setInterval(paint, 500);
}

let rendererBanner: HTMLElement | null = null;

function hideRendererBanner(): void {
  rendererBanner?.remove();
  rendererBanner = null;
}

function showRendererBanner(why: 'lost' | 'blank'): void {
  if (rendererBanner) return;
  const el = document.createElement('div');
  el.className = 'w21-renderer-banner';
  el.setAttribute('role', 'alert');
  const text = document.createElement('p');
  text.textContent =
    why === 'lost'
      ? 'The table stopped drawing — your phone took the graphics back. Reloading brings it back, and a ranked match picks up where you left it.'
      : 'The table is not drawing on this phone. Reloading may bring it back, and a ranked match picks up where you left it.';
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Reload';
  button.addEventListener('click', () => location.reload());
  el.append(text, button);
  document.body.append(el);
  rendererBanner = el;
}

/** Freeze or wake the table — the renderer and the lamp clock with it. */
function setShellIdle(idle: boolean): void {
  shellIdle = idle;
  runtime?.pixi.setIdle(idle);
  runtime?.table.setIdle(idle);
}

const handlers: UIHandlers = {
  onStart: () => {
    if (runtime === null) startRequested = true;
    else startUnguarded();
  },
  onPlayPile: (pile) => void playPile(pile),
  onUndo: () => void undoPlay(),
  onPause: (next) => {
    paused = next;
    if (next) accumulator = 0;
    // Pull the bed back behind the pause panel rather than cutting it: the
    // round has not ended, and silence would say it had.
    audio.setMusicDim(next);
  },
  /**
   * Play again.
   *
   * Replays the *mode*, not the deck. This used to restart the round with
   * whatever match was still in force, which for a challenge meant dealing the
   * same 52 cards again — and a deck you have already seen is not a game, it is
   * a second look at the answers. The scoreboard knew: the first result stood
   * and every replay was silently refused, so the player kept playing a deck
   * that could never count.
   *
   * A challenge therefore fetches a new one, a lobby opens a new lobby with a
   * new link to send, and solo deals a fresh shuffle.
   */
  onRestart: () => {
    if (runtime === null) {
      startRequested = true;
      return;
    }
    if (match.kind === 'staked') {
      // Another challenge at the same stake, not the same deck again — and the
      // stake has to be chosen afresh, because it is being paid afresh.
      void showCreateChallenge();
      return;
    }
    if (match.kind === 'async') {
      void beginMatch(takeAsyncMatch, 'Finding a challenge…');
      return;
    }
    if (match.kind === 'lobby') {
      void beginMatch(() => openLobbyMatch(globalThis.location.href), 'Opening a lobby…');
      return;
    }
    startUnguarded();
  },
  // The two staked routes. "Challenge a friend" opens a challenge and hands
  // back a code; "Take a challenge" finds one already open. Both now go through
  // the ladder rather than dealing an unstaked round, which is what the mode
  // cards have been promising since they started saying "Stake $CHAIN".
  onAsync: () => void showTakeChallenge(),
  onLobby: () => void showCreateChallenge(),
  onShare: () => void shareRound(),
  onShareChallenge: (view) => void shareSettledChallenge(view),
  onGuideStart: () => void runGuide(),
  onGuideEnd: () => stopGuide(),
  // Back to the title screen. The round is over and paid off, so the only
  // state that outlives it is the match — and leaving a spent challenge in
  // force would make the next "Play solo" deal that challenge's deck.
  onHome: () => {
    match = SOLO;
  },
  onSignOut: () => void signOut(),
  onSignedIn: (user) => signedIn(user),
  isRanked: () => match.challengeId !== null,
  // Hand the round in as it stands. A ranked round is ended by the server —
  // the clock is its, and a local tick was just a flash of 0:00 that the next
  // sync took back — and the settled step that comes back ends it here the
  // way any other step would. A solo round's clock is ours to run out.
  onForfeit: () => void forfeitRound(),
  onScreen: (screen) => setShellIdle(SHELL_SCREENS.has(screen)),
  onHistory: () => void showHistory(),
  rewards: { load: (cursor) => myRewards(cursor), inventory: () => inventory(), equip: async (kind, id) => {
      const r = await equipCosmetic(kind, id);
      if (r.ok) setInventory(r.value);
      return r;
    },
  },
  onBestScores: () => {
    void bestScores().then((r) => ui?.showBestScores(r.ok ? r.value : null));
  },
  onCreateChallenge: (entryFee, isPrivate, useTicket) => void createStaked(entryFee, isPrivate, useTicket === true),
  onTakeChallenge: (entryFee, useTicket) => void takeStaked(entryFee, useTicket === true),
  onTakeFee: queueAtFee,
  onTakeCode: (code) => void takeStakedCode(code),
  onPlayChallenge: () => playViewed(),
  onOpenChest: (id: string) => void openEarnedChest(id),
  onChestNews: chestNews,
  onChallengesChanged: () => challengesChanged.fire('notice'),
  onChestBalance: () => void openSession(),
  // A faucet claim paid: the pill, the home box and every screen's bar follow at once (the server's number, not a sum).
  onFaucetClaimed: (balance: number) => {
    if (account) setAccount({ ...account, balance });
  },
  onOpenChallenge: (id) => void openChallenge(id),
  onProfile: (username) => void showProfile(username),
  // No sign-up screen yet, so this is honest about that rather than silently
  // doing nothing: the guest keeps playing, and the note stays true.
  // Guest → account: the sign-up sheet (src/chest/signup-sheet.ts) asks from the profile, the
  // leaderboard, a chest, the rank-up; there is nothing for the host to do.
  onCreateAccount: () => undefined,
};

/* ------------------------------------------------------------------ *
 * Staked challenges
 * ------------------------------------------------------------------ */

/**
 * The fee ladder, fetched once.
 *
 * The server owns the list and it does not change between page loads, so the
 * screens that show it should not each pay for a round trip — but a failed
 * fetch must not leave the ladder empty, because a create screen with no stakes
 * on it is a dead end. Free play is the floor in every case, so that is the
 * fallback.
 */
let feeBounds: FeeBounds | null = null;

async function fees(): Promise<FeeBounds> {
  if (feeBounds) return feeBounds;
  const response = await entryFees();
  // A failed fetch falls back to the known bounds rather than to nothing; a half-deployed server
  // that sends no min/max leaves the defaults standing (`saneBounds`). The server re-checks every amount.
  const served = response.ok ? saneBounds(response.value) : null;
  if (served) feeBounds = served;
  return served ?? DEFAULT_BOUNDS;
}

/** The free-entry tickets the player holds, by wager, for the create and take screens. */
async function offerTickets(): Promise<void> {
  if (!CHESTS_ENABLED || !account) return;
  const held = await inventory();
  if (held.ok && (ui?.screen === 'create' || ui?.screen === 'take')) ui.setTickets(held.value.tickets);
}

/** Open the create screen, with a balance fresh enough to disable what it must. */
async function showCreateChallenge(): Promise<void> {
  ui?.showCreate(await fees(), account ? account.balance : null);
  void offerTickets();
  // Blockari's quiet line: how many they have created, and how many of those still wait for a taker —
  // and, under each wager chip, how many of their own are waiting at it ("N yours").
  void refreshChallengeCounts('create');
}

/** The player's own challenge counts, for the screen that is up (Create or Take) — it ignores an answer that arrives after the player left. */
const countsGate = createLatestGate();
async function refreshChallengeCounts(screen: 'create' | 'take'): Promise<void> {
  const latest = countsGate.begin();
  const r = await myChallengeCounts();
  // An older read never lands over a newer one.
  if (r.ok && latest() && ui?.screen === screen) {
    ui.setChallengeCounts(r.value);
    loadClock.stamp('my-counts');
  }
}

/** Open the take screen. The queue counts are the reason it is worth looking at. */
async function showTakeChallenge(prefill: string | null = null): Promise<void> {
  // The screen first, on the ladder already known, and the code in its slots
  // before any network answer: a share link is a promise the player is
  // holding, and a boot that takes the code out of the address bar and then
  // waits on two fetches before showing anything leaves them on the home
  // screen with nothing to show for it. The counts and the queue land in
  // place; a ladder that turns out to differ (it never has) re-lays the seg.
  const shown = feeBounds ?? DEFAULT_BOUNDS;
  ui?.showTake(shown, {}, account ? account.balance : null);
  if (prefill) ui?.prefillTake(prefill);
  void offerTickets();
  void refreshChallengeCounts('take');
  const [bounds, counts] = await Promise.all([fees(), queueCounts()]);
  if (!ui || ui.screen !== 'take') return;
  if (bounds.fees.join() !== shown.fees.join() || bounds.min !== shown.min || bounds.max !== shown.max) {
    ui.showTake(bounds, counts.ok ? counts.value.queues : {}, account ? account.balance : null);
    if (prefill) ui.prefillTake(prefill);
  } else if (counts.ok) ui.setQueues(counts.value.queues);
  await refreshQueue();
  startQueuePolling();
}

/** Every 8 s while the take screen is up and the tab is visible, and again on focus. */
const QUEUE_POLL_MS = 8000;
let queuePoll = 0;

const queueGate = createLatestGate();

async function refreshQueue(): Promise<void> {
  if (!ui || ui.screen !== 'take' || ui.takeBusy()) return;
  const fee = ui.takeFee();
  const latest = queueGate.begin();
  // The CUSTOM tile lists the amounts that are not a quick pick, whatever the page is on.
  const [open, counts] = await Promise.all([openQueue(fee > 0 ? fee : undefined, ui.takeCustom()), queueCounts()]);
  // A slower, older read must not put its queue back over a newer one.
  if (!latest()) return;
  if (!ui || ui.screen !== 'take') return;
  loadClock.stamp('queue');
  if (open.ok) ui.setQueue(open.value.open);
  if (counts.ok) ui.setQueues(counts.value.queues);
}

/** A new wager asks for its own queue, after a pause for typing. */
let feeTimer = 0;
function queueAtFee(): void {
  window.clearTimeout(feeTimer);
  feeTimer = window.setTimeout(() => void refreshQueue(), 300);
}

function startQueuePolling(): void {
  if (queuePoll) return;
  queuePoll = window.setInterval(() => {
    // Stopped by the screen going, not by a route hook: the interval checks
    // where it is and lets itself go.
    if (!ui || ui.screen !== 'take') {
      window.clearInterval(queuePoll);
      queuePoll = 0;
      return;
    }
    if (document.visibilityState === 'visible') void refreshQueue();
  }, QUEUE_POLL_MS);
}

/**
 * "My challenges changed" (see net/challenges-changed.ts): the app coming back to the foreground, or a notice that a
 * challenge ended. Every list of the player's own challenges that is open reads again, once per burst. The abandoned-seat
 * sweep settles challenges while the app sits in the background, and each of these lists is only the fetch that opened it.
 */
const challengesChanged = createChangedSignal();

challengesChanged.subscribe((cause) => {
  if (!ui) return;
  const jobs = jobsFor({
    visible: typeof document === 'undefined' || document.visibilityState === 'visible',
    signedIn: account !== null,
    screen: ui.screen,
    ownProfile: ui.profileIsOwn(),
    ownWagersOpen: ownWagersOpen(),
  });
  for (const job of jobs) {
    // Focus and visibility arrive together, and a list that was just read has nothing newer to say; a notice is never skipped.
    if (!loadClock.due(job, cause)) continue;
    switch (job) {
      case 'queue': void refreshQueue(); break;
      case 'my-counts': if (ui.screen === 'create' || ui.screen === 'take') void refreshChallengeCounts(ui.screen); break;
      case 'history': void refreshHistory(); break;
      case 'profile-recent': void refreshProfileRecent(); break;
      case 'profile-stats': void refreshProfileStats(); break;
      case 'home-recent': void refreshHomeRecent(); break;
      case 'own-wagers': refreshOwnWagers(); break;
    }
  }
});

if (typeof document !== 'undefined') watchForeground(challengesChanged, window, document);

/**
 * Accept a deal the server has made: the stake is already gone.
 *
 * Everything after this point is bookkeeping on a transaction that has already
 * happened, which is why the balance is taken from the deal rather than
 * re-fetched — the server computed it in the same transaction that took the
 * money, and a second request could answer from a cache written before it.
 */
async function acceptDeal(deal: StakedDeal, stake?: StakeFlight, potCoin?: Element | null): Promise<void> {
  match = stakedMatch(deal, 'creator');
  if (account) setAccount({ ...account, balance: deal.balance });
  // The stake's coin leaves the pill for the pot card, then the challenge page opens (Blockari's handoff
  // order: the flight is over before the screen changes, so it lands on something the player can see).
  await stake?.finish(potCoin ?? null);
  void openChallenge(deal.challengeId);
}

/** The challenge on the challenge page, so "Play your game" plays THAT one. */
let viewed: ChallengeView | null = null;

/** Show one challenge, fetched fresh. */
async function openChallenge(challengeId: string): Promise<void> {
  const view = await challengeView(challengeId);
  if (!view.ok) return;
  viewed = view.value;
  ui?.showChallenge(view.value);
}

/**
 * "Play your game" on the challenge page.
 *
 * The page can be reached with `match` pointing anywhere — at the last round,
 * or at solo after a page load — and `startRound()` deals whatever `match`
 * says. So the match is taken from the page: this challenge, its stake, its
 * code. Only a seat not yet played is dealt; a played one has nothing left
 * to play and the button is not shown for it.
 */
function playViewed(): void {
  const view = viewed;
  if (!view || view.me !== null) return;
  match = matchFromView(view);
  ui?.setChallengeNotice(null);
  // A seat played from its own page: if the round cannot be opened the page SAYS why and stays put, instead
  // of an unranked game the server knows nothing about.
  guardStart({
    start: () => startRound(),
    onStarted: (listener) => bus.on('game:start', listener),
    timeoutMs: PAID_START_MS,
    failed: (reason) => {
      console.error(`[main] a seat did not open (${reason})`);
      const code = notOpenedCode(reason);
      ui?.setChallengeNotice(
        code === 'round-already-played'
          ? 'You have already played this deck.'
          : code === 'round-finished'
            ? 'This challenge is already finished.'
            : code === 'round-closed'
              ? 'This challenge has closed, so it cannot be played any more.'
              : code === 'round-not-seated'
                ? 'Take this challenge first: you have no seat on it yet.'
                : 'Your game did not open. Check your connection and try again.',
      );
    },
  });
}

async function createStaked(entryFee: number, isPrivate: boolean, useTicket = false): Promise<void> {
  // The pill holds its number until the stake's coin has left it.
  const stake = ui?.beginStake();
  const deal = await createChallenge(entryFee, isPrivate, useTicket);
  if (!deal.ok) {
    void stake?.finish(null);
    ui?.failCreate(deal.error);
    return;
  }
  // The new challenge is one more of theirs waiting at this wager: the Create screen's chips are told now,
  // not on the next visit.
  void myChallengeCounts().then((r) => {
    if (r.ok) ui?.setChallengeCounts(r.value);
  });
  await acceptDeal(deal.value, stake, ui?.potCoin() ?? null);
}

/**
 * How long a take waits for the table to exist before it gives up — without charging. The first load of a
 * new bundle on a phone can take longer than the few seconds a player takes to reach the Take button.
 */
const TABLE_WAIT_MS = 20_000;

/** How long a PAID round may take to start before the player is told, rather than left on a spinner. */
const PAID_START_MS = 15_000;

/**
 * True once the table can deal — checked BEFORE a stake is taken.
 *
 * The take is charged by the server the instant it answers; the game starts afterwards, on this end. If
 * the table were not up by then `startRound` used to return quietly and hand the start to the end of
 * boot, and a player who gave up and reloaded was left with a paid seat and no game.
 */
async function tableIsUp(): Promise<boolean> {
  if (await whenReady(() => runtime !== null, TABLE_WAIT_MS)) return true;
  ui?.failTake('table-loading');
  return false;
}

/**
 * Start the round for a seat that has just been paid for, and make sure it either starts or SAYS it did
 * not: a rejected start, or none within `PAID_START_MS`, clears the spinner with a message and opens the
 * seat's challenge page, where "Play your game" plays it. (Not retried here: the server marks a seat
 * started on the first successful open, so a second open after a lost reply would burn it.)
 */
function beginPaidRound(challengeId: string): void {
  guardStart({
    start: () => startRound(),
    onStarted: (listener) => bus.on('game:start', listener),
    timeoutMs: PAID_START_MS,
    failed: (reason) => {
      console.error(`[main] a paid round did not start (${reason})`);
      ui?.failTake(notOpenedCode(reason));
      void openChallenge(challengeId);
    },
  });
}

async function takeStaked(entryFee: number, useTicket = false): Promise<void> {
  if (!(await tableIsUp())) return;
  const stake = ui?.beginStake();
  const deal = await takeChallenge(entryFee, useTicket);
  if (!deal.ok) {
    void stake?.finish(null);
    ui?.failTake(deal.error);
    return;
  }
  // Somebody is already waiting on this deck, so there is nothing to wait for:
  // straight to the table, and the scoreboard afterwards.
  match = stakedMatch(deal.value, 'taker');
  const potCoin = ui?.potCoin() ?? null;
  if (account) setAccount({ ...account, balance: deal.value.balance });
  // The stake's coin lands on the pot card, then the table opens.
  await stake?.finish(potCoin);
  beginPaidRound(deal.value.challengeId);
}

async function takeStakedCode(code: string): Promise<void> {
  if (!(await tableIsUp())) return;
  const stake = ui?.beginStake();
  const deal = await takeChallengeByCode(code);
  if (!deal.ok) {
    void stake?.finish(null);
    ui?.failTake(deal.error);
    return;
  }
  match = stakedMatch(deal.value, 'taker');
  const potCoin = ui?.potCoin() ?? null;
  if (account) setAccount({ ...account, balance: deal.value.balance });
  await stake?.finish(potCoin);
  beginPaidRound(deal.value.challengeId);
}

/* ------------------------------------------------------------------ *
 * The account
 * ------------------------------------------------------------------ */

/** Who is signed in. Null until the session is resolved, and after signing out. */
let account: Account | null = null;

/**
 * Establish a session, silently, on boot.
 *
 * A player who has been here before already has a uuid in local storage and is
 * signed straight back in as the guest who owns it. A new one is given that
 * same treatment with a fresh id. Either way nobody is asked to make an account
 * before they have seen the game — the sign-up screen exists for somebody who
 * wants their balance on a second device, which is a thing you want *after* you
 * care about the balance.
 *
 * Failure is silent and total: no account means no stake, the staking screens
 * say so, and solo play is unaffected because it never needed one.
 */
async function openSession(): Promise<void> {
  // A guest is minted only when the server SAYS nobody is signed in, never
  // because the question could not be asked.
  //
  // `/auth/me` answers 200 with `user: null` for a visitor with no session, so
  // `ok: false` means the call itself failed — a restart, a dropped
  // connection, a timeout. Treating that as "signed out" replaced the player's
  // account with a brand-new guest, taking their balance, rank and XP off the
  // screen and stranding the real account behind a cookie the client had just
  // overwritten. The capture harness caught it as a frame that changed
  // accounts halfway through a run; a player would have seen it as being
  // logged out at random, most likely right after a deploy.
  //
  // One retry, because the usual cause is a few seconds of API restart.
  let known = await whoAmI();
  if (!known.ok) {
    await new Promise((resolve) => setTimeout(resolve, 1200));
    known = await whoAmI();
  }
  if (!known.ok) return;

  if (known.value.user) {
    setAccount(known.value.user);
    if (known.value.dailyGranted > 0) ui?.showDailyGrant(known.value.dailyGranted);
    return;
  }
  const guest = await playAsGuest(legacyPlayerId());
  if (guest.ok) setAccount(guest.value.user);
  else if (guest.error === 'guest-rate-limited') {
    // Too many new players from this connection in the last hour (a shared network, or a script). Solo play is
    // unaffected; say so rather than leave a table with no account and no explanation.
    startUi.status('Too many new players have joined from this connection. Try again in a little while.', 'rose');
  }
}

function setAccount(next: Account | null): void {
  const was = account?.username ?? null;
  account = next;
  // The session is the identity. An old browser id, offered once to reclaim a pre-accounts history, has done
  // its job the moment an account is known (see net/session.ts).
  if (next) dropLegacyPlayerId();
  ui?.setAccount(next);

  // The feed belongs to an account, so it starts and stops with one. Keyed on
  // the username changing rather than on every setAccount: the balance moves
  // constantly, and tearing the stream down and rebuilding it on each of those
  // would drop notifications in the gap.
  if (next && next.username !== was) void ui?.notify.start();
  if (!next && was) {
    ui?.notify.stop();
    challengesChanged.cancel();
  }
  // The home card's recent list. Fired alongside rather than awaited: the card
  // is useful without it and a slow history query must not hold up a balance.
  if (next) void refreshRecent();
  else ui?.setRecent([]);
  // The chests: the pill's count, and — on home — any chest earned while away
  // arrives (Blockari's useHomeArrivals, a beat after home has come in).
  if (CHESTS_ENABLED && next && next.username !== was) {
    void refreshChests().then(() => homeArrival(true));
  }
  // The inventory moves with the account (a round spent a boost game): the header's boost chip follows it.
  if (CHESTS_ENABLED && next && next.username === was) void refreshInventory();
  if (CHESTS_ENABLED && !next) clearChests();
}

/** A beat after home has come in, so an arrival never lands on a half-drawn page. */
const HOME_ARRIVAL_MS = 450;

/** Home: any unopened chest whose arrival has not played arrives, a beat after the list is read (Blockari's useHomeArrivals). */
function homeArrival(allowDev: boolean): void {
  setTimeout(() => {
    if (ui?.screen !== 'start') return;
    // Registering renames the account: its chests arrive after the celebration, not under it.
    if (signupStore.getState().celebrating) return;
    const dev = allowDev ? devArrival() : null;
    if (dev) showArrival(dev, { dev: true });
    else showArrival(chestStore.getState().unopened);
  }, HOME_ARRIVAL_MS);
}

/**
 * A chest notice the chest list has not heard (the stream's, or the foreground re-read's after the app was away):
 * re-read the list so the pill counts it and, on home, its arrival plays (Blockari 3f31ff7). The list needs the
 * account's own read first, which `setAccount` does on sign-in.
 */
function chestNews(): void {
  if (!CHESTS_ENABLED || !account) return;
  void refreshChests().then(() => homeArrival(false));
}

const homeGate = createLatestGate();

/** The last few challenges, for the home card. Silent on failure. */
async function refreshRecent(): Promise<void> {
  const latest = homeGate.begin();
  const mine = await myChallenges();
  if (mine.ok && latest()) {
    ui?.setRecent(mine.value.challenges);
    loadClock.stamp('home-recent');
  }
}

/** Home is up and may be out of date: the Recent card again, in place (cards that are the same are left alone). */
async function refreshHomeRecent(): Promise<void> {
  const latest = homeGate.begin();
  const mine = await myChallenges();
  if (!mine.ok || !latest() || ui?.screen !== 'start') return;
  ui.refreshHomeRecent(mine.value.challenges);
  loadClock.stamp('home-recent');
}

const statsGate = createLatestGate();

/** The viewer's own profile is up: its stat tiles and money card read again, in place. */
async function refreshProfileStats(): Promise<void> {
  const name = account?.username;
  if (!name) return;
  const latest = statsGate.begin();
  const [profile, finance] = await Promise.all([profileOf(name), financeOf()]);
  if (!profile.ok || !latest() || !ui || ui.screen !== 'profile' || !ui.profileIsOwn()) return;
  ui.refreshProfileStats(profile.value, finance.ok ? finance.value : null);
  loadClock.stamp('profile-stats');
}

/**
 * Signed in from the sheet: a different account from the one this browser held (a guest's, usually).
 * The chests are the guest's until the new account's are read, so the old ones go first.
 */
function signedIn(user: Account): void {
  if (CHESTS_ENABLED) clearChests();
  setAccount(user);
  // `setAccount` re-reads the chests and starts the feed when the name changes; the daily top-up is
  // granted by the session read, which a sign-in is not.
  void whoAmI().then((r) => {
    if (!r.ok || !r.value.user || r.value.user.username !== user.username) return;
    setAccount(r.value.user);
    if (r.value.dailyGranted > 0) ui?.showDailyGrant(r.value.dailyGranted);
  });
}

async function signOut(abandonGuest = false): Promise<boolean> {
  const out = await logout(abandonGuest);
  // A guest that holds something is not signed out until the player has been shown it: nobody can sign back in.
  const verdict = signOutVerdict(out);
  if (verdict.kind === 'ask') {
    openSignOutConfirm(verdict.value, async () => {
      const done = await signOut(true);
      if (done) dismissSignup();
      return done;
    });
    return false;
  }
  setAccount(null);
  // Straight back to a guest, because being signed out of a game you can play
  // anonymously is a state with nothing in it. Signing out means "stop being
  // *this* account", not "stop playing".
  await openSession();
  return true;
}

/**
 * Show a profile. Omitting the name shows the signed-in player's own.
 *
 * The screen is opened before the fetch so the tap has an immediate answer;
 * a spinner that arrives instantly beats a page that appears half a second
 * after the press and looks like it was waiting to be sure.
 */
async function showProfile(username?: string, focusChallenge = false): Promise<void> {
  const name = username ?? account?.username;
  if (!name) return;
  ui?.openProfile();
  const profile = await profileOf(name);
  if (!profile.ok) {
    ui?.failProfile(
      profile.error === 'not-found' ? 'No such player.' : 'Could not load that profile.',
    );
    return;
  }
  const mine = profile.value.username === account?.username;
  // The private money figures, from the session only, for the player's own page.
  const finance = mine ? await financeOf() : null;
  ui?.showProfile(profile.value, mine, finance?.ok ? finance.value : null);
  if (mine) {
    loadClock.stamp('profile-stats');
    loadClock.stamp('profile-recent');
  }
  // A Challenge Me link (`/profile/<name>#challenge`): the "Play <name>" card, in view.
  if (focusChallenge) ui?.focusProfileChallenge();
}

const historyGate = createLatestGate();

async function showHistory(): Promise<void> {
  const latest = historyGate.begin();
  const mine = await myChallenges();
  if (!mine.ok || !latest()) return;
  ui?.showHistory(mine.value.challenges, mine.value.record);
  loadClock.stamp('history');
}

/** History is up and may be out of date: read it again and re-render in place (no blank, scroll kept). */
async function refreshHistory(): Promise<void> {
  const latest = historyGate.begin();
  const mine = await myChallenges();
  // A failed read keeps what is on screen; an older answer never replaces a newer one; the screen may have moved on.
  if (!mine.ok || !latest() || ui?.screen !== 'history') return;
  ui.refreshHistory(mine.value.challenges, mine.value.record);
  loadClock.stamp('history');
}

const recentGate = createLatestGate();

/** The viewer's own profile is up: its Recent challenges read again, in place. */
async function refreshProfileRecent(): Promise<void> {
  const latest = recentGate.begin();
  const mine = await myChallenges();
  if (!mine.ok || !latest() || !ui || ui.screen !== 'profile' || !ui.profileIsOwn()) return;
  ui.refreshProfileRecent(mine.value.challenges);
  loadClock.stamp('profile-recent');
}

/**
 * Set up a multiplayer round, then deal it.
 *
 * The network call happens on the start screen, where a spinner is honest and a
 * delay costs nothing. If it fails the player still gets a game — an unranked
 * one, with the reason on screen — because a scoreboard being down is not a
 * reason to refuse to deal cards.
 */
async function beginMatch(claim: () => Promise<Match>, pending: string): Promise<void> {
  startUi.busy(true);
  startUi.status(pending);
  try {
    match = await claim();
    if (match.degradedReason !== null) {
      startUi.status(`Offline — playing solo (${match.degradedReason})`, 'rose');
    } else if (match.lobby) {
      startUi.status(`Lobby ${match.lobby.code} — share the link when you are done`);
    } else if (match.target) {
      startUi.status(`Chasing ${match.target.name} on ${match.target.score.toLocaleString()}`);
    } else {
      startUi.status('Nobody has played this deck yet — set the target');
    }
  } finally {
    startUi.busy(false);
  }

  if (runtime === null) startRequested = true;
  else startUnguarded();
}

const QUALITY_PIN_KEY = 'wild21.quality';

/** The settings sheet's view of the quality tier, once the renderer is up. */
let qualitySetting: { get(): QualityChoice; set(choice: QualityChoice): void } | null = null;

function readQualityPin(): QualityChoice {
  try {
    const raw = localStorage.getItem(QUALITY_PIN_KEY);
    return raw === 'high' || raw === 'medium' || raw === 'low' ? raw : 'auto';
  } catch {
    return 'auto';
  }
}

function writeQualityPin(choice: QualityChoice): void {
  try {
    localStorage.setItem(QUALITY_PIN_KEY, choice);
  } catch {
    // Private mode: the pin holds for this session.
  }
}

async function boot(): Promise<void> {
  if (booted) return;
  booted = true;

  const host = resolveHost();

  // Mounted first: it paints the token background and puts the start screen up
  // while the renderer and the card atlas are still coming online.
  // `audio` is the mixer the pause screen's sound controls drive. Passing the
  // whole Audio object is fine — `AudioMixer` is the six methods and six getters
  // the control actually uses, so the UI stays unable to make a sound.
  if (CHESTS_ENABLED) registerChestOpener((id) => void openEarnedChest(id));
  ui = mountUI({
    handlers,
    autoGameOver: false,
    audio,
    // The sheet reads and pins through this; the governor behind it is built
    // with the renderer, later, so the object forwards to whatever is there.
    quality: {
      get: () => qualitySetting?.get() ?? readQualityPin(),
      set: (choice) => {
        writeQualityPin(choice);
        qualitySetting?.set(choice);
      },
    },
  });

  // The session starts here, before the renderer.
  //
  // It is two requests over the network and nothing on this screen waits for
  // them, so what decides when the balance appears is when the main thread is
  // free to run the continuation. Started after the card atlas it queued behind
  // a 200ms bake and a full deal and did not land for ten seconds — an empty
  // strip for long enough to look like it was never coming. Started here it
  // overlaps all of that.
  void openSession();
  void registerWorker();

  const unlock = (): void => {
    audio.init();
    void audio.unlock();
    // The first gesture is the only moment a browser will let audio start, so
    // it is also the moment to warm the beds — a round beginning is too late to
    // start downloading the track it wants to fade in. The warming itself
    // needs no gesture, though, and done inside the handler it was a 60 ms
    // task on the first touch — the touch that starts a scroll. It goes off
    // the handler to the next idle moment.
    const warm = (): void => audio.preloadMusic();
    if ('requestIdleCallback' in globalThis) globalThis.requestIdleCallback(warm, { timeout: 2000 });
    else globalThis.setTimeout(warm, 300);
    // Warmed, not started. The menu is silent: music belongs to a round, and a
    // bed that begins the moment somebody touches the page is playing over a
    // screen they may be reading for a minute before they press anything.
    // `game:start` brings it in under the riffle, which is where it belongs.
  };
  window.addEventListener('pointerdown', unlock, { once: true, passive: true });
  window.addEventListener('keydown', unlock, { once: true });
  teardown.push(() => {
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  });

  // Independent: the atlas rasterises on a 2D canvas and needs no GL context.
  //
  // The selective defocus is off by request. It was the only screen-space blur
  // in the chain, and because it sharpens the resolving pile and softens
  // *everything else*, the stock — which is never the resolving pile — went
  // soft on every clear. Read as two faults, it was one. The rest of the post
  // chain (bloom, aberration, vignette, grain, grade) is untouched.
  const [pixi] = await Promise.all([
    createApp(host, { postfx: { defocus: { enabled: false } } }),
    preloadCardTextures(),
  ]);

  // Pick a post-processing tier for this device and keep watching it. Until
  // now `setQuality` was never called at all, so a phone ran the same chain as
  // the desktop it was tuned on. See `@/pixi/quality`.
  const governor = new QualityGovernor({
    onChange: (quality) => {
      pixi.postfx.setQuality(quality);
      ui?.setQualityTier(quality);
    },
  });
  governorRef = governor;
  // The table going dead under a live HUD: kick a stalled loop (the app does),
  // and when the GPU context is gone for good say so instead of leaving a running
  // clock over a black felt. Never an automatic reload (a refresh does resume a ranked round, but the player
  // chooses it, and a solo round is gone with the page).
  teardown.push(
    pixi.onHealth((health) => {
      if (health.kind !== 'restored') {
        console.warn(`[w21] renderer ${health.kind}: ${health.ms} ms, attempt ${health.attempts}`);
      }
      if (health.kind === 'lost') showRendererBanner('lost');
      else if (health.kind === 'blank' && health.detail === 'still-black') showRendererBanner('blank');
      else if (health.kind === 'restored') hideRendererBanner();
    }),
  );
  if (new URLSearchParams(globalThis.location?.search ?? '').has('diag')) mountDiagOverlay(pixi, governor);
  teardown.push(pixi.onUpdate((dt) => governor.sample(dt)));
  // A tier pinned in Settings outlives the session; `auto` is the governor.
  const pinned = readQualityPin();
  if (pinned !== 'auto') governor.pin(pinned);
  qualitySetting = {
    get: () => governor.pinned ?? 'auto',
    set: (choice) => {
      governor.pin(choice === 'auto' ? null : choice);
      writeQualityPin(choice);
    },
  };

  let table = createTable();
  pixi.layers.table.addChild(table);

  // Air in the room. On the shadows layer it sits above the felt and above
  // every card shadow, and below every card — dust must never fog a face the
  // player is reading. zIndex 1 (the module's default) also keeps it last in
  // that layer, so it does not split the shadow batch.
  let atmosphere = createAtmosphere();
  pixi.layers.shadows.addChild(atmosphere.view);

  const pool = new CardPool({
    cardLayer: pixi.layers.cards,
    shadowLayer: pixi.layers.shadows,
    // Eight stock sprites plus a five-card pile, warmed before the first deal.
    prewarm: DEAL_VISIBLE + 5,
  });

  const cards: CardSource = {
    get(id: CardId): CardSprite {
      const sprite = pool.get(id);
      // The shader turns the card with a real perspective divide; the
      // choreographer narrows it with scale.x. Both would narrow it twice.
      sprite.squashFromFlip = false;
      return sprite;
    },
    recycle(id: CardId): void {
      pool.recycle(id);
    },
  };

  const choreo = new Choreographer({
    cards,
    pileWells: table.pileWells(),
    // VFX owns shake; see the header. Two sources would double a bust.
    autoShake: false,
  });

  // A hit-stop scales the choreographer's timelines, but the felt ripple and
  // the lamp clock in `table.ts` run off `gsap.ticker`, which a timeline time
  // scale does not touch. Without this the cards would freeze while the felt
  // carried on at full rate and the two would visibly tear apart.
  choreo.onTimeScale = (factor) => table.setTimeScale(factor);

  const vfx = new VFX({
    onBloom: (boost) => pixi.postfx.setBloomBoost(boost),
    onShake: (x, y, rotation) => pixi.stage.setShake(x, y, rotation),
    onPunch: (scale) => pixi.stage.setPunch(scale),
  });

  const totals = createPileTotals();

  // Both live on the vfx layer so the badges sit above the cards they describe
  // but below the numbers that fly off them.
  totals.zIndex = -10;
  vfx.view.zIndex = 0;
  pixi.layers.vfx.addChild(totals, vfx.view);

  // The streak rim and the badge callout go on `overlay`, whose parallax ratio
  // is 0. Everything on the play plane slides under the pointer to sell depth;
  // these must not, or the rim stops reading as the frame around the table and
  // starts reading as an object lying on it.
  pixi.layers.overlay.addChild(vfx.overlay);

  // Compile the shaders and upload the buffers now, while the start screen is
  // still up and a dropped frame costs nothing. Everything here is created on
  // first *render*, so without this the bill arrives during the opening deal
  // and again on the first streak and the first wild — which is exactly when
  // the game can least afford it. See `VFX.warm` and `CardPool.warm`.
  // Distinct faces, so the warm-up samples across the atlas rather than
  // uploading the same page a dozen times.
  pool.warm(createDeck(), WILD_CARDS[0] ?? 'js');
  totals.warm();
  vfx.warm();
  let warmFrames = 3;

  const unbindVfx = vfx.bind(bus, {
    // Bursts are fired from the play sequence instead, so they land when the
    // card lands rather than when the engine resolves.
    resolutions: false,
  });
  const unbindAudio = audio.bind(bus);
  teardown.push(unbindVfx, unbindAudio);

  const rt: Runtime = { pixi, table, pool, cards, choreo, vfx, totals, atmosphere };
  runtime = rt;
  // The screen that is up already — the start screen, or a challenge page a
  // link opened — was set before the table existed to be frozen under it.
  if (ui) setShellIdle(SHELL_SCREENS.has(ui.screen));

  // One loop. The app's own ticker advances this before it renders, so nothing
  // here ever draws a frame of its own.
  teardown.push(
    pixi.onUpdate((dt) => {
      // The camera first, so the hit test below is measured against the offset
      // this frame will actually be drawn with.
      pixi.stage.updateParallax(dt);
      // Then the pointer, so a hover lands on this frame rather than the next.
      flushPointer(rt);
      updateCards(dt);
      atmosphere.update(dt);
      vfx.update(dt);
      // After the effects it is waiting on, so a hold that expires this frame
      // does so having seen this frame's flourish.
      tickGameOver(dt);

      // Give the warm-up sprites their frames, then hand them back. Counted
      // here rather than on a timer so it is measured in rendered frames,
      // which is the only thing that actually compiles anything.
      if (warmFrames > 0 && --warmFrames === 0) {
        pool.cool();
        totals.cool();
      }
      // The streak colour bleeds off the table into the letterbox. Driven
      // after `vfx.update` so the spill uses this frame's aura strength rather
      // than last frame's, and from the aura's own eased values so the room
      // and the rim can never disagree about how bright the run is.
      pixi.stage.setSpill(vfx.aura.spillColour, vfx.aura.spillAmount);
      // And inward, onto the felt itself — the same colour from the same eased
      // level, so the room, the rim and the table can never disagree about what
      // the run is worth. `rt.table` rather than the captured `table`, because
      // an orientation change replaces it.
      rt.table.setStreakTint(vfx.aura.spillColour, vfx.aura.feltTint);
      pumpClock(dt);
    }),
  );

  // A phone that turns on its side is a different design rect, not the same one
  // letterboxed differently — `@/pixi/layout` has already switched profiles by
  // the time this runs. What is left is everything that baked the old rect.
  //
  // The table is rebuilt rather than adjusted because its geometry is compiled
  // into its fragment shader: the felt's extent, the lamp's aim and the pile
  // wells are GLSL literals, not uniforms. The dust field is rebuilt for the
  // same reason. Everything else — the cards, the popups, the bursts — derives
  // its position when it is drawn or fired, so cutting the board to the current
  // game state is enough to put it all in the right place.
  teardown.push(
    pixi.onRelayout(() => {
      // Land every animation FIRST, while the objects it is animating still
      // exist. `nudgePile` and `pulsePile` tween the table's own well
      // containers, and the choreographer holds those references until its jobs
      // finish — so destroying the table under a live job leaves `finalize`
      // writing `.x` to a destroyed container, which throws. The throw then
      // takes out whichever play sequence was in flight, and a play that dies
      // between `resolvePile` and `flipStockCard` leaves the stock face-down
      // with the board waiting for an input that never comes.
      choreo.fastForward();

      const previousTable = table;
      previousTable.destroy();
      table = createTable();
      pixi.layers.table.addChild(table);
      // The choreographer presses these on a tap; the old ones no longer exist.
      choreo.setPileWells(table.pileWells());

      const previousAtmosphere = atmosphere;
      atmosphere = createAtmosphere();
      pixi.layers.shadows.addChild(atmosphere.view);
      previousAtmosphere.dispose();

      rt.table = table;
      // Built under whatever screen is up: a rebuild on the home is a frozen one.
      table.setIdle(shellIdle);
      rt.atmosphere = atmosphere;

      totals.relayout();
      vfx.relayout();

      // Last: it reads the layout everything above has just been rebuilt for.
      cutToState();
    }),
  );

  installPointer(rt);
  installBus(rt);

  // Somebody opened a shared link. Join before the player can press anything,
  // so the start screen already knows it is a challenge — and take the code out
  // of the address bar once it is spent, or a later reload would silently
  // re-join a game that has already been played.
  // The score to beat. Fire-and-forget: it arrives when it arrives, the screen
  // shows nothing until it does, and a scoreboard that is down costs a line of
  // text rather than a start screen.
  void topScores().then((result) => {
    if (result.ok) ui?.setTarget(result.value);
  });

  // The session, in parallel with everything else on this screen.
  const sessionReady = openSession();
  void sessionReady;

  // A shared code — `?code=XXXXXX` from a challenge page, or the older
  // `?c=` — opens the take screen with the six slots filled and Join primary.
  // The address bar is cleared so a reload does not re-offer it.
  const shared = codeFromLocation(globalThis.location?.href ?? '');
  if (shared !== null) {
    clearCodeFromLocation();
    void showTakeChallenge(shared);
  }

  // A Challenge Me card's QR and link land on `/profile/<name>#challenge`: that player's public profile, scrolled to
  // its "Play <name>" card. The route has no page of its own (the host falls back to this bundle), so it is read
  // here, once the session is known (it decides whether the page is yours), and the address is put back to `/`.
  const deepProfile = profileFromPath(globalThis.location?.pathname ?? '/');
  if (deepProfile !== null) {
    const focus = wantsChallengeCard(globalThis.location?.hash ?? '');
    globalThis.history?.replaceState?.(null, '', `/${globalThis.location?.search ?? ''}`);
    const go = (): void => void showProfile(deepProfile, focus);
    void sessionReady.then(go, go);
  }

  // A tapped push lands on `/challenge/<id>` (a cold start) or arrives as a message from the worker
  // (the app was already open): both open that challenge's page, once the session is known.
  const pushPath = globalThis.location?.pathname ?? '/';
  if (openFromPushPath(pushPath, sessionReady)) globalThis.history?.replaceState?.(null, '', `/${globalThis.location?.search ?? ''}`);
  navigator.serviceWorker?.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: string; url?: string } | null;
    if (data?.type !== 'notification-click' || typeof data.url !== 'string') return;
    try {
      openFromPushPath(new URL(data.url, globalThis.location.origin).pathname, sessionReady);
    } catch {
      // a url we cannot read is a tap we cannot route
    }
  });

  // Blockari's `/leaderboard?board=…&period=…`: `?screen=leaderboard` opens it,
  // on the board and period asked for. Opened once the session is known, so the
  // first fetch already carries the player's own row.
  const wanted = new URLSearchParams(globalThis.location?.search ?? '');
  if (wanted.get('screen') === 'leaderboard') {
    const board = wanted.get('board');
    const period = wanted.get('period');
    void sessionReady.then(() =>
      ui?.showLeaderboard(
        isBoard(board) ? board : 'xp',
        period === 'all' ? 'all' : 'week',
      ),
    );
  }

  const invite = lobbyFromLocation(globalThis.location?.href ?? '');
  if (invite !== null) {
    clearLobbyFromLocation();
    startUi.busy(true);
    startUi.status('Joining the lobby…');
    match = await joinLobbyMatch(invite, globalThis.location.href);
    startUi.busy(false);
    if (match.degradedReason !== null) {
      startUi.status(`That lobby could not be opened (${match.degradedReason})`, 'rose');
    } else {
      const host = match.lobby?.hostName;
      startUi.status(
        match.target
          ? `${match.target.name} scored ${match.target.score.toLocaleString()} — beat it`
          : `${host ? `${host} ` : ''}challenged you to this deck`,
      );
      // The deck is already claimed and the only useful thing left to do is
      // deal it, so the screen stops offering the two ways in that would
      // discard it. Deliberately inside this branch: a link that failed to
      // open leaves all three, because there is nothing to accept.
      ui?.setStartInvite(true);
    }
  }

  if (import.meta.env.DEV) installDirector();

  void replayCeremony();
  if (import.meta.env.DEV) void replayFlights();

  // A ranked round this tab was in when it was reloaded comes first: it is still being played, on the server.
  // Not awaited: on a bad link it retries for as long as it takes, and the table must not wait on it. A start
  // the player asked for in the meantime is theirs, and the resume stands down for it (`roundToken`).
  void resumeLiveRound(rt);

  if (startRequested) {
    startRequested = false;
    startUnguarded();
  }
}

/**
 * The coin flights' dev replay, on the real app (the real bars, the real results screen), for review and
 * for the clips:
 *
 *   ?stakeflight=<fee>        opens the take screen and plays a stake of `fee` (default 100) leaving the pill
 *   ?winflight=<pot>          ends a round on the results screen with a win that paid `pot` (default 200)
 *      &order=before|during|after   where the balance refresh falls against the coins (default during)
 *
 * Visual only: no server call, no money moves — the balance the account reports is adjusted locally, the
 * way the server's answer would adjust it. `import.meta.env.DEV` is a literal Vite replaces at build time,
 * so none of this is in a production bundle.
 */
async function replayFlights(): Promise<void> {
  const url = new URL(globalThis.location?.href ?? 'http://localhost/');
  const stakeParam = url.searchParams.get('stakeflight');
  const winParam = url.searchParams.get('winflight');
  if (stakeParam === null && winParam === null) return;
  // The session has to be in before there is a balance to move.
  for (let i = 0; i < 100 && !account; i += 1) await new Promise((r) => setTimeout(r, 100));
  if (!account || !ui) return;
  const start = account.balance;

  if (stakeParam !== null) {
    const fee = Number(stakeParam) > 1 ? Number(stakeParam) : 100;
    ui.showTake(DEFAULT_BOUNDS, {}, start);
    await new Promise((r) => setTimeout(r, 900));
    const stake = ui.beginStake();
    const potCoin = ui.potCoin();
    setAccount({ ...account, balance: start - fee });
    await stake.finish(potCoin);
    return;
  }

  const pot = Number(winParam) > 0 ? Number(winParam) : 200;
  const order = url.searchParams.get('order') ?? 'during';
  ui.showGameOver({
    base: 6910, perfectGameBonus: 0, noBustBonus: 0, timeBonus: 750, total: 7660,
    busts: 1, clears: 19, bestStreak: 4, plays: 59, secondsRemaining: 103,
    cardsRemaining: 7, perfectGame: false, reason: 'deckEmpty', levelReached: 7,
  });
  await new Promise((r) => setTimeout(r, 1200));
  if (order === 'before') setAccount({ ...account, balance: start + pot });
  if (order === 'during') setTimeout(() => account && setAccount({ ...account, balance: start + pot }), 700);
  if (order === 'after') setTimeout(() => account && setAccount({ ...account, balance: start + pot }), 4200);
  ui.setVerdict(
    { outcome: 'win', opponent: 'nova', theirScore: 6140, mine: 7660, payout: pot, stake: pot / 2, theirLevel: 6 },
    { role: 'taker', challengeId: `dev-${Date.now()}`, share: false },
  );
}

/**
 * The ceremony's dev replay: `?ceremony=rank|level&from=<level>&to=<level>`,
 * with `&t=<ms>` to freeze one frame and `&q=high|medium|low` to see a tier.
 *
 * It plays over whatever is on screen, because that is what it does in a real
 * round, and it is the route the clips are captured from — a ceremony nobody
 * can replay on demand is a ceremony nobody can review.
 */
async function replayCeremony(): Promise<void> {
  const url = new URL(globalThis.location?.href ?? 'http://localhost/');
  const kind = url.searchParams.get('ceremony');
  if (kind !== 'rank' && kind !== 'level') return;
  const from = Number(url.searchParams.get('from') ?? (kind === 'rank' ? 15 : 11));
  const to = Number(url.searchParams.get('to') ?? from + 1);
  const plan = planCeremony(from, to);
  if (!plan) {
    console.warn(`[ceremony] no crossing between level ${from} and ${to}`);
    return;
  }
  if (plan.kind !== kind) {
    console.warn(`[ceremony] levels ${from} → ${to} are a ${plan.kind}-up, not a ${kind}-up`);
  }
  const pinned = url.searchParams.get('t');
  const quality = url.searchParams.get('q');
  const { playCeremony } = await import('@/ceremony/host');
  await playCeremony(plan, document.body, {
    ...(pinned === null ? {} : { pinMs: Number(pinned) }),
    ...(quality === 'high' || quality === 'medium' || quality === 'low' ? { quality } : {}),
  });
}

/**
 * Open a chest: the server applies its stored roll, and the screen animates
 * what came back.
 *
 * Opening is the server's write, not the animation's — a player who closes the
 * screen half way through has still been paid, and a retried request applies
 * nothing a second time. The balance moves, so the account is refreshed with it.
 */
async function openEarnedChest(id: string): Promise<void> {
  if (!CHESTS_ENABLED) return;
  const opened = await openChest(id);
  if (!opened.ok) return;
  settleOpened(opened.value);
  ui?.showChest(opened.value.chest);
  void openSession();
}

/** `/challenge/<id>` (what a push carries): open that challenge once the session is in. True when it was one. */
function openFromPushPath(path: string, sessionReady: Promise<unknown>): boolean {
  const match = /^\/challenge\/([0-9a-f-]{36})\/?$/i.exec(path);
  if (!match) return false;
  const id = match[1]!;
  void sessionReady.then(() => openChallenge(id));
  return true;
}

/** The code in a share link: `?code=` first, `?c=` for links already out there. */
function codeFromLocation(href: string): string | null {
  try {
    const url = new URL(href);
    const raw = url.searchParams.get('code') ?? url.searchParams.get('c');
    if (!raw) return null;
    const code = raw.toUpperCase();
    return /^[A-Z0-9]{6}$/.test(code) ? code : null;
  } catch {
    return null;
  }
}

function clearCodeFromLocation(): void {
  const history = globalThis.history;
  const location = globalThis.location;
  if (!history?.replaceState || !location) return;
  const url = new URL(location.href);
  if (!url.searchParams.has('code') && !url.searchParams.has('c')) return;
  url.searchParams.delete('code');
  url.searchParams.delete('c');
  history.replaceState(null, '', url.toString());
}

async function forfeitRound(): Promise<void> {
  const live = engine;
  if (!live || live.isOver) return;
  if (live instanceof RemoteEngine) {
    const done = await live.forfeit();
    if (!done) ui?.announce('Could not hand the round in — try again');
    return;
  }
  live.tick(ROUND_SECONDS + 1);
}

/**
 * The round clock, on a fixed step. A frame delta is whatever the display and
 * the GPU felt like today; 180 seconds is 180 seconds.
 */
function pumpClock(dt: number): void {
  if (!clockRunning || paused || engine === null) return;

  accumulator = Math.min(accumulator + dt, MAX_CLOCK_CATCHUP);
  while (accumulator >= CLOCK_STEP) {
    accumulator -= CLOCK_STEP;
    engine.tick(CLOCK_STEP);
    // `tick` can end the round mid-drain; the rest of the banked time is not
    // owed to anyone.
    if (engine.isOver) {
      accumulator = 0;
      return;
    }
  }
}

/** Full teardown. Used by the dev server on hot replace. */
function dispose(): void {
  for (const off of teardown.splice(0)) off();

  const rt = runtime;
  runtime = null;
  engine = null;
  snapshot = null;
  wildSprite = null;
  lastPlay = null;
  clockRunning = false;
  hoveredPile = -1;

  if (rt) {
    rt.choreo.dispose();
    rt.vfx.dispose();
    rt.totals.destroy();
    rt.atmosphere.dispose();
    rt.pool.destroy();
    rt.table.destroy();
    rt.pixi.destroy();
  }

  ui?.destroy();
  ui = null;
  booted = false;
}

/* ------------------------------------------------------------------ *
 * Capture director — development only
 * ------------------------------------------------------------------ */

/**
 * The handle the trailer capture rig drives the game through.
 *
 * Trailer footage has to be deterministic: the same 52 cards, played on the
 * same frames, every run — because the cut is locked to a musical grid and each
 * sound effect is placed against the exact frame a card lands on. A screen
 * recording gives none of that, and recovering the moment of impact from frame
 * luminance (which is all you can do when video is all you have) is guesswork
 * this project does not have to do. The engine already announces every landing
 * on the bus, so the rig reads the truth instead of inferring it.
 *
 * Nothing here is a second rendering path. `deal` stacks a deck the way the
 * walkthrough already does and hands it to the same `GameEngine`; the board,
 * the choreography and the effects are the ones a player sees. What the rig
 * gets that a player does not is a clock it controls and a log with timestamps
 * in it.
 *
 * `import.meta.env.DEV` is a literal Vite replaces at build time, so the whole
 * of this is dead code that the production bundle does not contain — checked by
 * grepping `dist/` for `__w21`, not assumed.
 */
interface DirectorEvent {
  /** `performance.now()` when the event fired. Under the rig's virtual clock
   *  this divides exactly by the frame time, which is the whole point. */
  t: number;
  type: string;
  pile?: number;
  card?: string;
  score?: number;
  streakLevel?: number;
  streakLabel?: string | null;
  points?: number;
  bonuses?: string[];
  total?: number;
}

function installDirector(): void {
  let events: DirectorEvent[] = [];
  let origin = 0;

  onCardLanded = (pile, card) => {
    events.push({
      t: Math.round((performance.now() - origin) * 1000) / 1000,
      type: 'card:land',
      pile,
      card,
    });
  };

  bus.onAny((event) => {
    const at = { t: Math.round((performance.now() - origin) * 1000) / 1000, type: event.type };
    if (event.type === 'card:play') {
      events.push({ ...at, pile: event.pile, card: event.card });
    } else if (event.type === 'pile:resolve') {
      const r = event.resolution;
      events.push({
        ...at,
        pile: r.pile,
        card: r.card,
        bonuses: [...r.bonuses],
        points: r.pointsGained,
        streakLevel: r.streakLevel,
        streakLabel: r.streakLabel,
        score: r.score,
      });
    } else if (event.type === 'game:over') {
      events.push({ ...at, total: event.final.total, score: event.final.base });
    } else if (event.type !== 'tick') {
      events.push(at);
    }
  });

  const director = {
    /** True once the renderer, the atlas and the warm-up have all landed. */
    ready: (): boolean => runtime !== null,

    /** The post chain, for the sharpness probes. */
    fx: () => runtime?.pixi.postfx,

    /** Kill the frame loop from underneath the app, as a stall would; the watchdog should revive it. */
    stallLoop: (): void => runtime?.pixi.app.ticker.stop(),

    /**
     * Black the table out as a broken GPU would: `chain` through a filter on the same chain the post
     * effects are in (detaching the chain cures it), `hard` with a black cover nothing can detach.
     */
    blank: async (mode: 'chain' | 'hard' | 'off'): Promise<void> => {
      const { ColorMatrixFilter, Graphics } = await import('pixi.js');
      const pixi = runtime?.pixi;
      if (!pixi) return;
      if (mode === 'chain') {
        const f = new ColorMatrixFilter();
        f.matrix = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0];
        pixi.stage.root.filters = [...(pixi.stage.root.filters as unknown[] as never[]), f];
      } else if (mode === 'hard') {
        const g = new Graphics().rect(0, 0, 4000, 4000).fill(0x000000);
        g.label = 'w21-blackout';
        pixi.app.stage.addChild(g);
      } else {
        pixi.app.stage.getChildByLabel('w21-blackout')?.destroy();
      }
    },

    /** Deal a stacked deck onto a clean board and start the round. */
    deal(deck: readonly CardId[]): boolean {
      const rt = runtime;
      if (!rt) return false;
      stopGuide();
      match = SOLO;
      resetBoard(rt);
      ui?.showGame();
      origin = performance.now();
      events = [];
      if (engine instanceof RemoteEngine) engine.dispose();
      engine = new GameEngine(bus, { deck });
      engine.start();
      return true;
    },

    /** Place the face-up card on a pile, exactly as a tap would. */
    play: (pile: number): Promise<void> => playPile(pile),

    /**
     * Set the music level, for the audio capture pass.
     *
     * The trailer brings its own bed, and two beds fight. Recording the round
     * with the game's music at zero leaves a stem of nothing but the sound
     * design — which is the part worth having, since it is what a player
     * actually hears a 21 as.
     */
    music: (level: number): void => audio.setMusicVolume(level),

    /** Resolve once nothing on the table is moving. */
    settle: (): Promise<void> => waitForBoard(),

    /** Run the clock out on a local round — the results screen, without the three minutes. */
    end(): boolean {
      if (!engine || engine instanceof RemoteEngine || engine.isOver) return false;
      engine.tick(ROUND_SECONDS + 1);
      return true;
    },

    busy: (): boolean => isBusy(),

    state: () => ({
      screen: ui?.screen ?? null,
      phase: engine?.phase ?? null,
      score: snapshot?.score ?? 0,
      streakLevel: snapshot?.streakLevel ?? 0,
      busts: snapshot?.busts ?? 0,
      secondsRemaining: snapshot?.secondsRemaining ?? 0,
      activeCard: snapshot?.activeCard ?? null,
      piles: snapshot?.piles.map((pile) => [...pile]) ?? [],
      stock: snapshot?.stock.length ?? 0,
      running,
    }),

    /**
     * The active card as it is DRAWN, against what the engine says: is it visible and turned up? The
     * fuzz rig asks this once the board has gone quiet — a card the engine can play that the player
     * cannot see is the bug this exists to catch.
     */
    drawn: (): { id: string; flip: number; visible: boolean; alpha: number; busy: boolean; choreoBusy: boolean } | null => {
      const id = engine && !engine.isOver ? (engine.snapshot().activeCard ?? null) : null;
      if (id === null || !runtime) return null;
      const view = runtime.cards.get(id);
      return { id, flip: view.flip, visible: view.visible, alpha: view.alpha, busy: isBusy(), choreoBusy: runtime.choreo.isBusy };
    },

    /**
     * How sharp the table is being drawn right now: the renderer's resolution, the canvas's backing store
     * against its CSS box (a mismatch is a stretched, soft canvas), and the governor's tier and scale.
     */
    render: () => {
      const c = runtime?.pixi.canvas;
      const r = runtime?.pixi.app.renderer;
      const css = c?.getBoundingClientRect();
      return {
        t: Math.round(performance.now()),
        resolution: r?.resolution ?? null,
        backing: c ? [c.width, c.height] : null,
        css: css ? [Math.round(css.width), Math.round(css.height)] : null,
        stretch: c && css && css.width > 0 ? +(c.width / (css.width * (globalThis.devicePixelRatio || 1))).toFixed(3) : null,
        quality: governorRef?.quality ?? null,
        dpr: globalThis.devicePixelRatio || 1,
      };
    },

    /** Everything the engine has announced since `deal`, timestamped. */
    events: (): DirectorEvent[] => events,

    /** The clock the rig converts to frame numbers. */
    now: (): number => performance.now() - origin,
  };

  (globalThis as unknown as Record<string, unknown>).__w21 = director;
}

void boot().catch((error: unknown) => {
  console.error('[main] boot failed', error);
});

if (import.meta.hot) {
  import.meta.hot.dispose(dispose);
}
